import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, SectionPad, Badge } from "../../components/ui-extras";
import { Plus, Trash, PencilSimple, Copy, Question, EnvelopeSimple } from "@phosphor-icons/react";
import EmailSendModal from "../../components/EmailSendModal";
import QuickEmailReportModal, { priceListDefaultSubject, priceListDefaultBody, isEditedPriceListSubject } from "../../components/QuickEmailReportModal";
import { startDownload, backendDownloadUrl, downloadFromBackend } from "../../lib/download";
import { displayProduct } from "../../lib/products";
import FuelingByCityChart from "../../components/FuelingByCityChart";
import MarginUploadModal from "../../components/MarginUploadModal";
import MarginRuleHistory from "../../components/MarginRuleHistory";
import BackdatedConfirmModal, { isBackdated } from "../../components/BackdatedConfirmModal";
import FuelCardForm from "../../components/FuelCardForm";
import { formatCard } from "../../lib/cardDisplay";
import PortalUsersSection from "../../components/admin/PortalUsersSection";
import BulkCardUploadModal from "../../components/BulkCardUploadModal";

// ---------- inline help tooltip ----------
// Tiny `(?)` icon used next to field labels. Uses native `title` attribute so
// it works without any popover dependency and matches the existing pattern in
// this file (see the price-report column toggles below).
function HelpHint({ text, testId }) {
  return (
    <span
      tabIndex={0}
      role="button"
      title={text}
      aria-label={text}
      data-testid={testId}
      className="inline-flex items-center justify-center align-middle ml-1 w-4 h-4 rounded-full text-zinc-400 hover:text-zinc-700 cursor-help focus:outline-none focus:ring-1 focus:ring-zinc-400"
    >
      <Question size={12} weight="bold" />
    </span>
  );
}

// ---------- product options per network ----------
//
// Each network exposes the products that actually flow through it on
// the import side, so a Pipeline customer profile doesn't accidentally
// offer ESSO-only products (RFR, DSL_EFF_LS) in the margin dropdown.
//
// The label suffix hints at the unit semantics the operator sees in the
// margin field below:
//   ESSO     DEFD → final price ($/L)   — replace mode
//   Pipeline DEFD → margin (¢/L)        — add mode
//   USA      DEFD → margin (¢/Gallon)   — add mode (rendered via the

// Phase 3E-4a1 — Admin compact toggle for country-aware Petro-Pass
// lane authorization. Reads/writes `customer.authorized_lanes` via
// the existing `PUT /api/customers/{id}/authorized-lanes` (admin-only)
// endpoint. Never mutates margin rules or price_report_networks.
//
// Phase 3E-4a2 — Recent Pricing Lane Access Changes list rendered under
// the toggle. Reads from `GET /customers/{id}/authorized-lanes/history`
// which surfaces the `admin_audit` rows written by the PUT.
function PetroPassLanesToggle({ customer, hasUsMargin }) {
  const [lanes, setLanes] = useState(customer?.authorized_lanes || []);
  const [busy, setBusy] = useState(false);
  const [events, setEvents] = useState([]);
  const isUsOn = lanes.some(
    (l) => (l.network || "").toLowerCase() === "petro_pass"
           && (l.country || "").toUpperCase() === "US",
  );
  const isCaOn = lanes.some(
    (l) => (l.network || "").toLowerCase() === "petro_pass"
           && (l.country || "").toUpperCase() === "CA",
  );

  useEffect(() => {
    if (!customer?.customer_id) return;
    let alive = true;
    api.get(`/customers/${customer.customer_id}/authorized-lanes/history?limit=10`)
      .then((r) => { if (alive) setEvents(r.data?.events || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [customer?.customer_id, lanes]);

  async function toggleLane(cty) {
    if (!customer?.customer_id) return;
    const CTY = cty.toUpperCase();
    const isOn = CTY === "US" ? isUsOn : isCaOn;
    const laneLabel = CTY === "US" ? "Petro-Pass USA USD/GAL" : "Petro-Pass Canada CAD/L";
    const next = isOn
      ? lanes.filter(
          (l) => !((l.network || "").toLowerCase() === "petro_pass"
                    && (l.country || "").toUpperCase() === CTY))
      : [...lanes, { network: "petro_pass", country: CTY }];
    // USA margin advisory retained; CA has no margin-gate advisory today.
    if (!isOn && CTY === "US" && !hasUsMargin) {
      if (!window.confirm(
        "This customer has no Petro-Pass USA margin rule configured. Enabling the lane now will still result in zero customer-visible rows until a margin is added. Continue?"
      )) return;
    } else {
      const label = isOn ? "Disable" : "Enable";
      if (!window.confirm(
        `${label} ${laneLabel} for ${customer.company_name}?\n\n` +
        `${isOn
          ? "Customer will lose access to this pricing lane. Their margin rule will be preserved."
          : "Customer will be able to view/download this pricing lane."}`
      )) return;
    }
    setBusy(true);
    try {
      const res = await api.put(
        `/customers/${customer.customer_id}/authorized-lanes`,
        { lanes: next },
      );
      setLanes(res.data?.authorized_lanes || next);
      toast.success(`${laneLabel} ${isOn ? "disabled" : "enabled"}.`);
    } catch (e) {
      toast.error(`Failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  }

  const humanEvent = (ev) => {
    const added = (ev.extra?.added || []);
    const removed = (ev.extra?.removed || []);
    if (added.length && !removed.length) {
      const lane = added[0];
      return { action: "Enabled", lane: `${lane.network}/${lane.country}` };
    }
    if (removed.length && !added.length) {
      const lane = removed[0];
      return { action: "Disabled", lane: `${lane.network}/${lane.country}` };
    }
    if (added.length && removed.length) {
      return { action: "Changed", lane: `+${added.length}/-${removed.length}` };
    }
    return { action: "Updated", lane: "—" };
  };

  return (
    <div className="mt-6" data-testid="petro-pass-lanes-section">
      <div className="overline mb-2">PETRO-PASS AUTHORIZED LANES</div>
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button" onClick={() => toggleLane("CA")} disabled={busy}
          data-testid="petro-pass-ca-toggle"
          className={`px-4 py-2 text-xs font-mono uppercase tracking-widest border ${
            isCaOn
              ? "bg-emerald-600 text-white border-emerald-600"
              : "bg-white text-zinc-600 border-zinc-200"
          }`}
        >
          Petro-Pass Canada CAD/L {isCaOn ? "· ENABLED" : "· DISABLED"}
        </button>
        <button
          type="button" onClick={() => toggleLane("US")} disabled={busy}
          data-testid="petro-pass-us-toggle"
          className={`px-4 py-2 text-xs font-mono uppercase tracking-widest border ${
            isUsOn
              ? "bg-emerald-600 text-white border-emerald-600"
              : "bg-white text-zinc-600 border-zinc-200"
          }`}
        >
          Petro-Pass USA USD/GAL {isUsOn ? "· ENABLED" : "· DISABLED"}
        </button>
        <span className="text-xs text-zinc-500">
          Independent lanes. Backend enforces 403 per-country when disabled.
        </span>
      </div>
      <div className="text-[11px] text-zinc-500 mt-2">
        Enabling one country does not enable the other — they are stored
        as separate <code>&#123;network:petro_pass, country:CA|US&#125;</code>
        entries in <code>authorized_lanes</code> and priced from their
        own margin rules (CAD/L vs USD/GAL).
      </div>
      {events.length > 0 && (
        <div className="mt-4" data-testid="petro-pass-lanes-history">
          <div className="overline mb-1 text-[10px]">
            RECENT PRICING LANE ACCESS CHANGES
          </div>
          <ul className="text-xs font-mono text-zinc-700 space-y-1">
            {events.map((ev, i) => {
              const h = humanEvent(ev);
              const when = ev.created_at
                ? new Date(ev.created_at).toLocaleString()
                : "—";
              return (
                <li key={i} className="flex gap-3">
                  <span className="w-40 truncate text-zinc-500">{when}</span>
                  <span className={`w-20 ${
                    h.action === "Enabled" ? "text-emerald-700" :
                    h.action === "Disabled" ? "text-rose-700" :
                    "text-zinc-600"}`}>{h.action}</span>
                  <span className="text-zinc-700">{h.lane}</span>
                  <span className="text-zinc-500 truncate">
                    by {ev.user_email || ev.user_id || "system"}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}


//   country=US branch in the form, not the product dropdown)
function productsForNetwork(network) {
  if (network === "esso") {
    return [
      { value: "DIESEL_LS", label: "Diesel LS" },
      { value: "DSL_EFF_LS", label: "DSL EFF LS" },
      { value: "RFR", label: "RFR" },
      // Product label kept unit-neutral. Unit is derived from country/lane
      // at pricing time; stored canonical value remains `DEFD`.
      { value: "DEFD", label: "DEFD" },
    ];
  }
  if (network === "pipeline") {
    // May 15 2026 fix: Pipeline now also exposes DEFD/DEF for margin
    // editing. Previously locked to Diesel only, which made it
    // impossible to set the Pipeline DEFD margin (¢/L) from the UI.
    return [
      { value: "DIESEL_LS", label: "Diesel" },
      { value: "DIESEL_DYED", label: "Diesel Co" },
      { value: "DEFD", label: "DEFD" },
    ];
  }
  return [
    { value: "ANY", label: "ANY (all products)" },
    { value: "DIESEL_LS", label: "Diesel LS / Diesel" },
    { value: "DSL_EFF_LS", label: "DSL EFF LS" },
    { value: "RFR", label: "RFR" },
    { value: "DEFD", label: "DEFD" },
    // P4-GAS-MARGIN-CREATE-UI-FIX R1 (2026-09-17). Single margin-family
    // key; the backend `pricing._GAS_TX_PRODUCTS` whitelist applies
    // this rule to every gasoline grade (GAS_REG/GAS_MID/GAS_PREM).
    // Petro-Pass Canada only — Esso/Pipeline lists above intentionally
    // omit GAS (no gasoline flow on those networks).
    { value: "GAS", label: "GAS (REG / MID / PREM)" },
  ];
}


export default function CustomerDetail() {
  const { customerId } = useParams();
  const [customer, setCustomer] = useState(null);
  const [margins, setMargins] = useState([]);
  const [cards, setCards] = useState([]);
  const [editingCard, setEditingCard] = useState(null);
  const [showBulkCards, setShowBulkCards] = useState(false);
  const [txs, setTxs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [showMargin, setShowMargin] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showReport, setShowReport] = useState(null); // "price" | "margin" | null
  const [showEmailQuick, setShowEmailQuick] = useState(null); // "price_list" | "transaction_report" | null
  // Date defaults for the Price / Margin report modal — pinned to
  // **America/Toronto** (EST/EDT). Pricing snapshots are taken on
  // Toronto-day boundaries throughout the rest of the app, so the
  // operator's UI must agree with the price engine.
  //
  // Price reports are forward-looking (operators send today for prices
  // that take effect TOMORROW), so the price modal defaults to
  // tomorrow EST. Margin reports are backward-looking (auditing
  // applied margins), so they default to today EST.
  const _torontoYmd = (offsetDays = 0) => {
    const d = new Date(Date.now() + offsetDays * 86400000);
    return d.toLocaleDateString("en-CA", { timeZone: "America/Toronto" });
  };
  const _torontoToday = () => _torontoYmd(0);
  const _torontoTomorrow = () => _torontoYmd(1);
  const [reportFrom, setReportFrom] = useState(_torontoTomorrow());
  const [reportTo, setReportTo] = useState(_torontoTomorrow());
  const [reportRows, setReportRows] = useState(null);
  const [reportMissingDates, setReportMissingDates] = useState([]);
  const [reportConflicts, setReportConflicts] = useState([]);
  const [reportTaxSummary, setReportTaxSummary] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  // Price-report network filter: 'both' | 'esso' | 'pipeline'.
  // When set to a single network, the download endpoint emits a per-supplier
  // file (separate filename + only that network's rows) so admins can mail
  // Esso vs Pipeline price lists independently.
  const [reportNetwork, setReportNetwork] = useState("both");
  const emptyMargin = {
    customer_id: customerId, country: "CA", network: "ANY", product_canonical: "DIESEL_LS",
    scope: "default", station_id: "", station_name: "", city: "", province: "",
    pricing_mode: "add", margin_cpl: 0, effective_date: new Date().toISOString().slice(0, 10),
  };
  const [mForm, setMForm] = useState(emptyMargin);
  // When non-null, the margin modal is in EDIT mode for this rule id (PUT instead of POST).
  // Multi-select scope helpers are disabled in edit mode — you edit one rule at a time.
  const [editingMarginId, setEditingMarginId] = useState(null);
  // Phase 3E-9Q.2 — backdated confirm modal state (shared by add + edit + copy).
  const [backdated, setBackdated] = useState(null);  // { open, effectiveDate, contextLabel, ruleCount, retry, busy }
  // Inline cpl edit (one rule at a time): { id, value } while editing.
  const [inlineEdit, setInlineEdit] = useState(null);
  // Sort the margin rules table by clicking column headers. Click toggles asc/desc.
  // Default = effective_date desc (most recent first), then scope, then product.
  const [sortKey, setSortKey] = useState("effective_date");
  const [sortDir, setSortDir] = useState("desc");
  // Admin-only profit summary (revenue + GP over a window).
  const [profit, setProfit] = useState(null);
  const [profitDays, setProfitDays] = useState(90);
  // When profitDays === "custom", these dates are used instead.
  const [profitFrom, setProfitFrom] = useState("");
  const [profitTo, setProfitTo] = useState("");
  // Margin rules: row-level multi-select for bulk-copy. (Different from the
  // city/station multi-select inside the modal — that one's for *creating*
  // many rules at once; this one's for copying existing rules.)
  const [selectedRuleIds, setSelectedRuleIds] = useState([]);
  const [copyToDate, setCopyToDate] = useState("");
  const [showCopyPrompt, setShowCopyPrompt] = useState(false);
  const [stations, setStations] = useState([]);
  const [cities, setCities] = useState([]);
  // Margin Excel upload flow: { stage: 'idle' | 'parsing' | 'preview' | 'committing', preview, ... }
  const [marginUpload, setMarginUpload] = useState(null);
  // Multi-select: when scope === "city", user can select N cities to apply the
  // same margin rule to. Shift-click selects a range; Ctrl/Cmd-click toggles a
  // single entry; plain click resets to a single city. One POST per city.
  const [selectedCities, setSelectedCities] = useState([]);
  const [cityFilter, setCityFilter] = useState("");
  const [lastCityClickIdx, setLastCityClickIdx] = useState(null);
  // Stations multi-select (mirrors the city multi-select UX).
  const [selectedStations, setSelectedStations] = useState([]);
  const [stationFilter, setStationFilter] = useState("");
  const [lastStationClickIdx, setLastStationClickIdx] = useState(null);
  const [marginInput, setMarginInput] = useState("0"); // text buffer for $ vs cents display
  // May 2026: country filter tabs (ALL / CA / US) for the margin-rules
  // table. Operator spec — admin needs a dedicated view of "USA TA Petro
  // Margins" so US rules don't get lost among hundreds of CA rules.
  // ALL = no filter (current behaviour). CA = country == "CA" (legacy
  // rules without country are treated as CA — same fallback the backend
  // pricing engine uses). US = country == "US".
  const [marginCountryFilter, setMarginCountryFilter] = useState("ALL");
  // "Resolve all shadows": confirmation dialog state. shadowedRules is computed
  // each render from `margins` so the count stays accurate after deletes.
  const [showShadowConfirm, setShowShadowConfirm] = useState(false);

  // Compute the set of station-scope rules that are currently shadowed by a
  // more-recent city-scope rule for the same network/product/city. We expose
  // this as a memoised value so both the table (per-row badge) and the bulk
  // "Resolve shadows" button use the SAME definition — no risk of the button
  // deleting rules the table didn't flag.
  const shadowedRules = React.useMemo(() => {
    const cityRuleByKey = {};
    margins.forEach((m) => {
      if (m.scope !== "city" || !m.city) return;
      const key = `${m.network}|${m.product_canonical}|${(m.city || "").toUpperCase()}`;
      const prev = cityRuleByKey[key];
      if (!prev || (m.effective_date || "") > (prev.effective_date || "")) {
        cityRuleByKey[key] = m;
      }
    });
    const out = [];
    margins.forEach((m) => {
      if (m.scope !== "station" || !m.station_city) return;
      const key = `${m.network}|${m.product_canonical}|${(m.station_city || "").toUpperCase()}`;
      const cityRule = cityRuleByKey[key];
      if (cityRule && (cityRule.effective_date || "") > (m.effective_date || "")) {
        out.push({ rule: m, shadowedBy: cityRule });
      }
    });
    return out;
  }, [margins]);

  const load = async () => {
    const [c, m, crd, t, i] = await Promise.all([
      api.get(`/customers/${customerId}`),
      api.get("/margins", { params: { customer_id: customerId } }),
      api.get("/cards", { params: { customer_id: customerId } }),
      api.get("/transactions", { params: { customer_id: customerId, limit: 50 } }),
      api.get("/invoices", { params: { customer_id: customerId } }),
    ]);
    setCustomer(c.data); setMargins(m.data); setCards(crd.data);
    setTxs(t.data); setInvoices(i.data);
    // Reset bulk-row selection when reloading — protects against stale ids.
    setSelectedRuleIds([]);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [customerId]);

  // Load admin-only profit summary independently of `load()` so changing the window
  // doesn't refetch the entire detail page.
  useEffect(() => {
    if (!customerId) return;
    const params = profitDays === "custom"
      ? (profitFrom && profitTo ? { date_from: profitFrom, date_to: profitTo } : null)
      : { days: profitDays };
    if (!params) { setProfit(null); return; }  // wait for both custom dates
    api.get(`/customers/${customerId}/profit-summary`, { params })
      .then(({ data }) => setProfit(data))
      .catch(() => setProfit(null));
  }, [customerId, profitDays, profitFrom, profitTo]);

  const addMargin = async () => {
    // Phase 3E-9Q.2 — backdated preflight. If effective_date < today
    // (server-derived on retry via `?confirm_backdated=true`), pop the
    // reusable BackdatedConfirmModal FIRST so the operator explicitly
    // acknowledges intent. Backend still 409-guards on any bypass.
    const eff = mForm.effective_date;
    if (isBackdated(eff)) {
      const ruleCount = mForm.scope === "city"
        ? Math.max(selectedCities.length, 1)
        : mForm.scope === "station"
          ? Math.max(selectedStations.length, 1)
          : 1;
      setBackdated({
        open: true,
        effectiveDate: eff,
        contextLabel: editingMarginId
          ? `Edit margin rule for ${customer?.company_name || customerId}`
          : `Create ${ruleCount} margin rule${ruleCount === 1 ? "" : "s"} for ${customer?.company_name || customerId}`,
        customerCount: 1,
        ruleCount,
        retry: async () => {
          setBackdated((b) => b && { ...b, busy: true });
          try {
            await _doSaveMargin("?confirm_backdated=true");
            setBackdated(null);
          } catch (e) {
            toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Failed");
            setBackdated((b) => b && { ...b, busy: false });
          }
        },
      });
      return;
    }
    // Not backdated — normal path.
    try {
      await _doSaveMargin("");
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.code === "BACKDATED_MARGIN_CONFIRMATION_REQUIRED") {
        // Backend detected backdate we missed (clock skew) — surface modal.
        setBackdated({
          open: true,
          effectiveDate: d.effective_date,
          contextLabel: `Save margin rule for ${customer?.company_name || customerId}`,
          customerCount: 1,
          ruleCount: 1,
          retry: async () => {
            setBackdated((b) => b && { ...b, busy: true });
            try {
              await _doSaveMargin("?confirm_backdated=true");
              setBackdated(null);
            } catch (err) {
              toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || "Failed");
              setBackdated((b) => b && { ...b, busy: false });
            }
          },
        });
        return;
      }
      toast.error(d?.message || d || "Failed");
    }
  };

  // P4-PP-CA-MARGIN-CREATE-SCOPE-CORRECTION R1 (2026-XX).
  // Canonicalize PP-CA "default" scope to the backend's canonical
  // "country_default" tier used by pricing.lookup_customer_margin
  // (PP-CA-COUNTRY-DEFAULT-LOOKUP-IMPL-R1, pricing.py:467). The
  // operator-facing scope label is unchanged (still "Default (all
  // stations)"); only the persisted payload switches to the resolver's
  // canonical value. Other network/country combinations are untouched
  // — Esso CA / Pipeline CA / ANY CA keep scope="default"; PP-US keeps
  // its existing scope="country_default"; US paths untouched.
  const _canonicalizeMarginPayload = (payload) => {
    if (!payload) return payload;
    if ((payload.country || "CA") === "CA"
        && (payload.network || "") === "petro_pass"
        && (payload.scope || "") === "default") {
      return { ...payload, scope: "country_default" };
    }
    return payload;
  };

  // Inner implementation — does the actual API calls. Split out so the
  // backdated preflight can retry with `?confirm_backdated=true`.
  const _doSaveMargin = async (extraQuery) => {
      if (editingMarginId) {
        // Edit mode — single rule, PUT. Sync the city/station the user clicked
        // in the multi-select back into the form payload.
        const payload = _canonicalizeMarginPayload({ ...mForm });
        if (payload.scope === "city") payload.city = selectedCities[0] || mForm.city;
        if (payload.scope === "station") payload.station_id = selectedStations[0] || mForm.station_id;
        const { data: putRes } = await api.put(`/margins/${editingMarginId}${extraQuery}`, payload);
        // May 2026 PRICING LOCK: surface reprice counts on every margin
        // edit so the operator immediately sees how many open
        // transactions just shifted. Zero-repriced is suspicious enough
        // to warn — it usually means the rule's scope (network /
        // country / product / dates) doesn't actually match any rows.
        const reprSummary = putRes?._repriced || {};
        const reprCount = Number(reprSummary.repriced || 0);
        if (reprCount > 0) {
          toast.success(
            `Margin rule updated · Prices updated for ${reprCount} open transaction${reprCount === 1 ? "" : "s"}; invoiced transactions unchanged.`
          );
        } else {
          toast.warning(
            "Margin rule updated — but 0 open transactions matched this rule's scope. Double-check the network / country / product / effective date."
          );
        }
      } else if (mForm.scope === "city") {
        if (selectedCities.length === 0) {
          return toast.error("Select at least one city");
        }
        const results = await Promise.allSettled(
          selectedCities.map((c) => api.post(`/margins${extraQuery}`, _canonicalizeMarginPayload({ ...mForm, city: c })))
        );
        const ok = results.filter((r) => r.status === "fulfilled").length;
        const fail = results.length - ok;
        if (ok > 0) toast.success(`Saved ${ok} city rule${ok === 1 ? "" : "s"}${fail ? ` · ${fail} failed` : ""}`);
        if (fail > 0 && ok === 0) toast.error("All saves failed");
      } else if (mForm.scope === "station") {
        if (selectedStations.length === 0) {
          return toast.error("Select at least one station");
        }
        const results = await Promise.allSettled(
          selectedStations.map((sid) => api.post(`/margins${extraQuery}`, _canonicalizeMarginPayload({ ...mForm, station_id: sid })))
        );
        const ok = results.filter((r) => r.status === "fulfilled").length;
        const fail = results.length - ok;
        if (ok > 0) toast.success(`Saved ${ok} station rule${ok === 1 ? "" : "s"}${fail ? ` · ${fail} failed` : ""}`);
        if (fail > 0 && ok === 0) toast.error("All saves failed");
      } else {
        const { data: postRes } = await api.post(`/margins${extraQuery}`, _canonicalizeMarginPayload(mForm));
        const reprSummary = postRes?._repriced || {};
        const reprCount = Number(reprSummary.repriced || 0);
        if (reprCount > 0) {
          toast.success(
            `Margin rule added · Prices updated for ${reprCount} open transaction${reprCount === 1 ? "" : "s"}; invoiced transactions unchanged.`
          );
        } else {
          toast.warning(
            "Margin rule added — but 0 open transactions matched this rule's scope. Verify network / country / product / effective date."
          );
        }
      }
      setShowMargin(false);
      setEditingMarginId(null);
      setMForm(emptyMargin);
      setSelectedCities([]);
      setCityFilter("");
      setLastCityClickIdx(null);
      setSelectedStations([]);
      setStationFilter("");
      setLastStationClickIdx(null);
      load();
  };

  const editMargin = (m) => {
    // Pre-fill the modal with this rule's values. We use the same modal so
    // network/product/scope dependent fields stay consistent.
    //
    // May 15 2026 fix: also carry `country` and `pricing_mode` into the
    // form. Without these, editing a Pipeline DEFD ¢/L rule would lose
    // its `pricing_mode='add'` and editing a USA TA Petro rule would
    // forget it was country=US — both made the rule effectively
    // un-editable from the UI.
    setEditingMarginId(m.margin_id);
    setMForm({
      customer_id: customerId,
      country: (m.country || "CA").toUpperCase(),
      network: m.network || "ANY",
      product_canonical: m.product_canonical || "DIESEL_LS",
      // P4-PP-CA-MARGIN-CREATE-SCOPE-CORRECTION R1: map the resolver's
      // canonical PP-CA `country_default` back to the UI's "default"
      // option value so the Scope select displays "Default (all
      // stations)" when editing. Submit canonicalizes it back. Other
      // scopes / networks / countries pass through unchanged.
      scope: (((m.country || "CA").toUpperCase() === "CA")
              && (m.network || "") === "petro_pass"
              && (m.scope || "") === "country_default")
        ? "default"
        : (m.scope || "default"),
      station_id: m.station_id || "",
      city: m.city || "",
      province: m.province || "",
      margin_cpl: Number(m.margin_cpl) || 0,
      pricing_mode: m.pricing_mode || "add",
      effective_date: m.effective_date || new Date().toISOString().slice(0, 10),
    });
    // Pre-seed the multi-select with the existing target so the chosen city /
    // station shows as the active row in the listbox while editing.
    setSelectedCities(m.scope === "city" && m.city ? [m.city] : []);
    setSelectedStations(m.scope === "station" && m.station_id ? [m.station_id] : []);
    setCityFilter("");
    setStationFilter("");
    setLastCityClickIdx(null);
    setLastStationClickIdx(null);
    setShowMargin(true);
  };
  const delMargin = async (id) => {
    await api.delete(`/margins/${id}`); toast.success("Removed"); load();
  };

  // Open the create-margin modal pre-filled with one existing rule's values
  // but in CREATE mode and with the date defaulting to "tomorrow" — so users
  // can copy a rule to the next date in two clicks (Copy → Save).
  const copyRule = (m) => {
    const next = new Date(m.effective_date || new Date().toISOString().slice(0, 10));
    next.setDate(next.getDate() + 1);
    const nextDateStr = next.toISOString().slice(0, 10);
    setEditingMarginId(null); // CREATE mode
    setMForm({
      customer_id: customerId,
      country: (m.country || "CA").toUpperCase(),
      network: m.network || "ANY",
      product_canonical: m.product_canonical || "DIESEL_LS",
      // P4-PP-CA-MARGIN-CREATE-SCOPE-CORRECTION R1: mirror the edit-mode
      // scope canonicalization so copying a PP-CA country_default rule
      // pre-populates the UI's "default" option.
      scope: (((m.country || "CA").toUpperCase() === "CA")
              && (m.network || "") === "petro_pass"
              && (m.scope || "") === "country_default")
        ? "default"
        : (m.scope || "default"),
      station_id: m.station_id || "",
      city: m.city || "",
      province: m.province || "",
      margin_cpl: Number(m.margin_cpl) || 0,
      pricing_mode: m.pricing_mode || "add",
      effective_date: nextDateStr,
    });
    setSelectedCities(m.scope === "city" && m.city ? [m.city] : []);
    setSelectedStations(m.scope === "station" && m.station_id ? [m.station_id] : []);
    setCityFilter(""); setStationFilter("");
    setLastCityClickIdx(null); setLastStationClickIdx(null);
    setShowMargin(true);
  };

  // Bulk-copy: prompts for one target date, then POSTs a fresh rule for each
  // selected source rule with that date. Source rules are unchanged.
  const bulkCopyRules = async () => {
    if (selectedRuleIds.length === 0) return;
    if (!copyToDate) { toast.error("Pick a target date"); return; }
    const sources = margins.filter((m) => selectedRuleIds.includes(m.margin_id));
    const results = await Promise.allSettled(sources.map((m) => api.post("/margins", {
      customer_id: customerId,
      network: m.network,
      product_canonical: m.product_canonical,
      scope: m.scope,
      station_id: m.station_id || null,
      city: m.city || null,
      province: m.province || null,
      margin_cpl: m.margin_cpl,
      effective_date: copyToDate,
    })));
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const fail = results.length - ok;
    if (ok > 0) toast.success(`Copied ${ok} rule${ok === 1 ? "" : "s"} → ${copyToDate}${fail ? ` · ${fail} failed` : ""}`);
    if (fail > 0 && ok === 0) toast.error("All copies failed");
    setSelectedRuleIds([]);
    setCopyToDate("");
    setShowCopyPrompt(false);
    load();
  };

  // Inline cpl save. Sends the full PUT body but only `margin_cpl` differs;
  // the backend keeps customer_id pinned and re-uppercases city/prov as usual.
  const saveInlineMargin = async (m, displayVal) => {
    const isDefd = m.product_canonical === "DEFD";
    const n = parseFloat(displayVal);
    if (isNaN(n)) {
      setInlineEdit(null);
      return toast.error("Enter a valid number");
    }
    const cpl = isDefd ? Math.round(n * 100 * 100) / 100 : Math.round(n * 100) / 100;
    if (cpl === m.margin_cpl) { setInlineEdit(null); return; }  // no-op
    try {
      const payload = {
        customer_id: m.customer_id,
        network: m.network,
        product_canonical: m.product_canonical,
        scope: m.scope,
        station_id: m.station_id || null,
        city: m.city || null,
        province: m.province || null,
        margin_cpl: cpl,
        effective_date: m.effective_date,
      };
      await api.put(`/margins/${m.margin_id}`, payload);
      toast.success(`Updated to ${isDefd ? `$${(cpl / 100).toFixed(2)}/L` : `${cpl.toFixed(2)} ¢/L`}`);
      setInlineEdit(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
      setInlineEdit(null);
    }
  };

  const saveCard = async () => {
    if (!editingCard.card_number || !editingCard.card_number.trim()) {
      return toast.error("Card number is required");
    }
    const body = {
      network: editingCard.network,
      card_number: editingCard.card_number.trim(),
      customer_id: customerId,
      status: editingCard.status || "active",
      notes: editingCard.notes || "",
      truck_id: editingCard.truck_id || null,
      driver_id: editingCard.driver_id || null,
      unit: editingCard.unit || "",
      phone: (editingCard.phone || "").trim(),
    };
    try {
      if (editingCard.card_id) await api.put(`/cards/${editingCard.card_id}`, body);
      else await api.post("/cards", body);
      toast.success(editingCard.card_id ? "Card updated" : "Card added");
      setEditingCard(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save card"); }
  };

  const deleteCard = async (c) => {
    if (!window.confirm(`Delete card ${formatCard(c.card_number, c.network, c.last4)}? Transactions on this card will lose their customer link.`)) return;
    try {
      await api.delete(`/cards/${c.card_id}`);
      toast.success(`Removed card ${formatCard(c.card_number, c.network, c.last4)}`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };

  // Load stations / cities when scope or network changes while margin dialog is open
  useEffect(() => {
    if (!showMargin) return;
    if (mForm.scope === "station") {
      api.get("/stations", { params: { network: mForm.network, province: mForm.province || undefined } })
        .then(({ data }) => setStations(data));
    }
    if (mForm.scope === "city") {
      api.get("/cities", { params: { network: mForm.network } })
        .then(({ data }) => setCities(data));
    }
  }, [showMargin, mForm.scope, mForm.network, mForm.province]);

  // When scope/network/product changes, reset the displayed margin input.
  // For ESSO DEFD (replace mode), the input is $/L (margin_cpl/100). For
  // Pipeline/TA Petro DEFD and all diesel-family rules the input is ¢/L
  // or ¢/Gal stored directly in margin_cpl.
  useEffect(() => {
    const isDefd = mForm.product_canonical === "DEFD";
    const isUsa = (mForm.country || "CA").toUpperCase() === "US";
    const isEssoDefd = isDefd && !isUsa && mForm.network === "esso";
    setMarginInput(isEssoDefd
      ? (mForm.margin_cpl / 100).toFixed(2)
      : mForm.margin_cpl.toFixed(2)
    );
    // eslint-disable-next-line
  }, [mForm.product_canonical, mForm.network, mForm.country, showMargin]);

  if (!customer) return <div className="p-8 font-mono text-sm">LOADING…</div>;

  return (
    <SectionPad>
      <div className="mb-4">
        <Link to="/customers" className="overline hover:underline" data-testid="back-to-customers">← CUSTOMERS</Link>
      </div>
      <PageHeader
        title={customer.company_name}
        subtitle={`CUSTOMER / ${customer.customer_id.toUpperCase()}`}
        testId="customer-detail-header"
        actions={
          <>
            <button className="gf-btn gf-btn-primary" onClick={() => setShowPrefs(true)} data-testid="edit-customer-button">
              <PencilSimple size={14}/> Edit customer
            </button>
            <button
              className="gf-btn gf-btn-outline"
              onClick={() => {
                // Price reports look FORWARD — default to tomorrow EST.
                // Re-pinning on every modal open also covers leaving
                // the tab open across midnight.
                const t = _torontoTomorrow();
                setReportFrom(t);
                setReportTo(t);
                setShowReport("price");
              }}
              data-testid="open-price-report"
            >
              View price report
            </button>
            <button
              className="gf-btn gf-btn-outline"
              onClick={() => {
                // Margin reports are backward-looking — default to today EST.
                const t = _torontoToday();
                setReportFrom(t);
                setReportTo(t);
                setShowReport("margin");
              }}
              data-testid="open-margin-report"
            >
              Margin report
            </button>
            <button
              className="gf-btn gf-btn-outline"
              onClick={() => setShowEmailQuick("price_list")}
              data-testid="quick-email-price-list-button"
              title="Open the price report — download / preview or email to the customer"
            >
              <EnvelopeSimple size={14}/> Email Price Report
            </button>
            <button
              className="gf-btn gf-btn-outline"
              onClick={() => setShowEmailQuick("transaction_report")}
              data-testid="quick-email-transaction-report-button"
              title="Open the transactions report — download / preview or email to the customer"
            >
              <EnvelopeSimple size={14}/> Transactions
            </button>
            <button
              className="gf-btn gf-btn-outline text-red-700 border-red-300 hover:bg-red-50"
              onClick={() => setShowDelete(true)}
              data-testid="delete-customer-button"
              title="Delete this customer"
            >
              <Trash size={14}/> Delete
            </button>
            <Badge variant={customer.status === "active" ? "success" : "muted"}>{customer.status}</Badge>
          </>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <button onClick={() => setShowPrefs(true)} className="gf-card gf-card-body text-left hover:border-zinc-400 transition-colors group relative" data-testid="card-contact">
          <div className="overline flex items-center justify-between">CONTACT <PencilSimple size={12} className="text-zinc-300 group-hover:text-zinc-700"/></div>
          <div className="text-sm mt-1">{customer.contact_name || <span className="text-zinc-400">—</span>}</div>
          <div className="text-xs text-zinc-500">{customer.contact_email || customer.phone || <span className="text-zinc-400">click to add</span>}</div>
        </button>
        <button onClick={() => setShowPrefs(true)} className="gf-card gf-card-body text-left hover:border-zinc-400 transition-colors group" data-testid="card-billing">
          <div className="overline flex items-center justify-between">BILLING <PencilSimple size={12} className="text-zinc-300 group-hover:text-zinc-700"/></div>
          <div className="text-sm mt-1 font-mono">{(customer.billing_days || []).join(", ").toUpperCase() || <span className="text-zinc-400">—</span>}</div>
          <div className="text-xs text-zinc-500">{customer.billing_email || <span className="text-zinc-400">click to add</span>}</div>
        </button>
        <button onClick={() => setShowPrefs(true)} className="gf-card gf-card-body text-left hover:border-zinc-400 transition-colors group" data-testid="card-address">
          <div className="overline flex items-center justify-between">ADDRESS <PencilSimple size={12} className="text-zinc-300 group-hover:text-zinc-700"/></div>
          <div className="text-sm mt-1">{customer.address || <span className="text-zinc-400">—</span>}</div>
          <div className="text-xs text-zinc-500 font-mono">{customer.province}</div>
        </button>
        <button onClick={() => setShowPrefs(true)} className="gf-card gf-card-body text-left hover:border-zinc-400 transition-colors group" data-testid="card-tax-credit">
          <div className="overline flex items-center justify-between">TAX / CREDIT <PencilSimple size={12} className="text-zinc-300 group-hover:text-zinc-700"/></div>
          <div className="text-sm mt-1 font-mono">{(customer.tax_rate * 100).toFixed(2)}% · ${(customer.credit_limit || 0).toLocaleString()}</div>
          <div className="text-xs text-zinc-500">Non-listed +{customer.non_listed_surcharge_cpl} CPL</div>
        </button>
      </div>

      {/* Admin-only revenue + GP card. Customer portal never renders this — see CustomerDashboard. */}
      <div className="gf-card mb-6" data-testid="profit-summary-card">
        <div className="gf-card-header">
          <h3>
            Revenue &amp; profit{" "}
            <span className="text-xs text-zinc-400">
              {profitDays === "custom"
                ? (profitFrom && profitTo ? `· ${profitFrom} → ${profitTo}` : "· pick a date range")
                : `· last ${profitDays}d`}
            </span>
          </h3>
          <div className="flex items-center gap-2">
            {profitDays === "custom" && (
              <>
                <input
                  type="date"
                  className="gf-input text-xs"
                  value={profitFrom}
                  onChange={(e) => setProfitFrom(e.target.value)}
                  data-testid="profit-date-from"
                />
                <span className="text-xs text-zinc-400">→</span>
                <input
                  type="date"
                  className="gf-input text-xs"
                  value={profitTo}
                  onChange={(e) => setProfitTo(e.target.value)}
                  data-testid="profit-date-to"
                />
              </>
            )}
            <select
              className="gf-input text-xs"
              value={profitDays}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "custom") {
                  // Seed custom range with today / today-30d so the inputs aren't empty.
                  if (!profitFrom || !profitTo) {
                    const today = new Date();
                    const past = new Date();
                    past.setDate(today.getDate() - 30);
                    setProfitFrom(past.toISOString().slice(0, 10));
                    setProfitTo(today.toISOString().slice(0, 10));
                  }
                  setProfitDays("custom");
                } else {
                  setProfitDays(parseInt(v, 10));
                }
              }}
              data-testid="profit-window"
            >
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
              <option value={180}>180 days</option>
              <option value={365}>365 days</option>
              <option value="custom">Custom…</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border-t border-zinc-200">
          <div className="p-4 border-r border-zinc-200">
            <div className="overline">REVENUE</div>
            <div className="text-lg font-semibold mt-1" data-testid="profit-revenue">
              ${(profit?.totals?.revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div className="text-xs text-zinc-500">customer billed</div>
          </div>
          <div className="p-4 border-r border-zinc-200">
            <div className="overline">GROSS PROFIT</div>
            <div className="text-lg font-semibold mt-1 text-emerald-700" data-testid="profit-gp">
              ${(profit?.totals?.gp || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div className="text-xs text-zinc-500">rack → customer × qty</div>
          </div>
          <div className="p-4 border-r border-zinc-200">
            <div className="overline">MARGIN %</div>
            <div className="text-lg font-semibold mt-1" data-testid="profit-margin-pct">
              {(profit?.totals?.margin_pct || 0).toFixed(2)}%
            </div>
            <div className="text-xs text-zinc-500">GP / revenue</div>
          </div>
          <div className="p-4 border-r border-zinc-200">
            <div className="overline">LITRES</div>
            <div className="text-lg font-semibold mt-1" data-testid="profit-litres">
              {(profit?.totals?.qty || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} L
            </div>
            <div className="text-xs text-zinc-500">{profit?.totals?.count || 0} fuelings</div>
          </div>
          <div className="p-4">
            <div className="overline">$/L AVG</div>
            <div className="text-lg font-semibold mt-1" data-testid="profit-cpl-avg">
              ${profit?.totals?.qty > 0 ? (profit.totals.revenue / profit.totals.qty).toFixed(4) : "—"}
            </div>
            <div className="text-xs text-zinc-500">average customer rate</div>
          </div>
        </div>
      </div>

      <FuelingByCityChart customerId={customerId} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="gf-card">
          <div className="gf-card-header">
            <h3>
              {marginCountryFilter === "US"
                ? "Esso USD/GAL Margins"
                : marginCountryFilter === "CA"
                ? "Canada Margins"
                : "Margin rules"}
              <span className="text-xs text-zinc-400">
                {" · "}
                {marginCountryFilter === "ALL"
                  ? margins.length
                  : margins.filter((m) =>
                      marginCountryFilter === "US"
                        ? (m.country || "CA").toUpperCase() === "US"
                        : (m.country || "CA").toUpperCase() === "CA"
                    ).length}
              </span>
            </h3>
            <div className="flex items-center gap-2">
              {/* Country filter tabs — May 2026 operator spec */}
              <div className="inline-flex rounded-md border" data-testid="margin-country-filter">
                {["ALL", "CA", "US"].map((c) => (
                  <button
                    key={c}
                    onClick={() => setMarginCountryFilter(c)}
                    data-testid={`margin-country-tab-${c}`}
                    className={`px-3 py-1 text-xs font-mono ${
                      marginCountryFilter === c
                        ? "bg-amber-500 text-white"
                        : "bg-white text-zinc-600 hover:bg-zinc-50"
                    }`}
                  >
                    {c === "ALL" ? "All" : c === "US" ? "Esso USD/GAL" : "Canada"}
                  </button>
                ))}
              </div>
              {selectedRuleIds.length > 0 && (
                <>
                  <span className="text-xs text-amber-600 font-mono" data-testid="margin-bulk-selected-count">
                    {selectedRuleIds.length} selected
                  </span>
                  <button
                    className="gf-btn gf-btn-outline"
                    onClick={() => {
                      // Default copy date = max(rule effective dates)+1
                      const maxDate = margins
                        .filter((m) => selectedRuleIds.includes(m.margin_id))
                        .map((m) => m.effective_date)
                        .filter(Boolean)
                        .sort()
                        .pop();
                      const next = new Date(maxDate || new Date().toISOString().slice(0, 10));
                      next.setDate(next.getDate() + 1);
                      setCopyToDate(next.toISOString().slice(0, 10));
                      setShowCopyPrompt(true);
                    }}
                    data-testid="margin-bulk-copy-button"
                  >
                    <Copy size={14}/> Copy {selectedRuleIds.length} → date
                  </button>
                  <button
                    className="gf-btn gf-btn-ghost"
                    onClick={() => setSelectedRuleIds([])}
                    data-testid="margin-bulk-clear"
                  >Clear</button>
                </>
              )}
              <button className="gf-btn gf-btn-outline" onClick={() => setShowMargin(true)} data-testid="add-margin-button"><Plus size={14}/> Add rule</button>
              <button
                className="gf-btn gf-btn-ghost"
                onClick={async () => {
                  // Always re-download the template fresh — guarantees the
                  // dropdowns include any customer/station added since the
                  // last download.
                  try {
                    const filename = await downloadFromBackend("/margins/template.xlsx", {});
                    toast.success(`Template downloaded · ${filename}`);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Could not download template");
                  }
                }}
                data-testid="margin-template-download"
                title="Download a fresh Excel template (with up-to-date dropdowns)"
              >Template</button>
              <button
                className="gf-btn gf-btn-outline"
                onClick={() => setMarginUpload({ stage: "idle" })}
                data-testid="margin-upload-open"
                title="Upload margins via filled-in Excel template"
              >Upload Excel</button>
              {shadowedRules.length > 0 && (
                <button
                  className="gf-btn gf-btn-outline"
                  style={{ borderColor: "#f59e0b", color: "#b45309" }}
                  onClick={() => setShowShadowConfirm(true)}
                  data-testid="resolve-shadows-button"
                  title={`${shadowedRules.length} station rule${shadowedRules.length === 1 ? "" : "s"} shadowed by newer city rules`}
                >
                  Resolve {shadowedRules.length} shadow{shadowedRules.length === 1 ? "" : "s"}
                </button>
              )}
            </div>
          </div>
          <div
            className="overflow-auto"
            style={{ maxHeight: 480 }}
            data-testid="margin-rules-scroll"
          >
            {(() => {
              const sortHeader = (label, key, extraClass = "") => {
                const active = sortKey === key;
                const arrow = active ? (sortDir === "asc" ? " ↑" : " ↓") : "";
                return (
                  <th
                    className={`${extraClass} cursor-pointer select-none`}
                    onClick={() => {
                      if (sortKey === key) {
                        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
                      } else {
                        setSortKey(key);
                        setSortDir("asc");
                      }
                    }}
                    data-testid={`margin-sort-${key}`}
                    title="Click to sort"
                  >
                    {label}<span className="text-amber-600 font-mono">{arrow}</span>
                  </th>
                );
              };
              const sorted = [...margins]
                .filter((m) => {
                  if (marginCountryFilter === "ALL") return true;
                  const c = (m.country || "CA").toUpperCase();
                  return c === marginCountryFilter;
                })
                .sort((a, b) => {
                const va = a[sortKey] ?? "";
                const vb = b[sortKey] ?? "";
                let cmp;
                if (typeof va === "number" && typeof vb === "number") {
                  cmp = va - vb;
                } else {
                  cmp = String(va).localeCompare(String(vb));
                }
                return sortDir === "asc" ? cmp : -cmp;
              });
              // Build a city-rule index so we can flag station rules being shadowed
              // by a more-recent city rule for the same network/product/city.
              // Key: `${network}|${product_canonical}|${CITY_UPPER}` → most recent rule
              const cityRuleByKey = {};
              margins.forEach((m) => {
                if (m.scope !== "city" || !m.city) return;
                const key = `${m.network}|${m.product_canonical}|${(m.city || "").toUpperCase()}`;
                const prev = cityRuleByKey[key];
                if (!prev || (m.effective_date || "") > (prev.effective_date || "")) {
                  cityRuleByKey[key] = m;
                }
              });
              const computeShadow = (m) => {
                if (m.scope !== "station" || !m.station_city) return null;
                const key = `${m.network}|${m.product_canonical}|${(m.station_city || "").toUpperCase()}`;
                const cityRule = cityRuleByKey[key];
                if (!cityRule) return null;
                if ((cityRule.effective_date || "") > (m.effective_date || "")) {
                  return cityRule;
                }
                return null;
              };
              return (
            <table className="gf-table">
              <thead style={{ position: "sticky", top: 0, zIndex: 1, background: "white" }}>
                <tr>
                  <th style={{ width: 32 }}>
                    <input
                      type="checkbox"
                      checked={margins.length > 0 && selectedRuleIds.length === margins.length}
                      onChange={(e) => setSelectedRuleIds(e.target.checked ? margins.map((m) => m.margin_id) : [])}
                      data-testid="margin-select-all"
                      title="Select all rules"
                    />
                  </th>
                  {sortHeader("COUNTRY", "country")}
                  {sortHeader("SCOPE", "scope")}
                  {sortHeader("NETWORK", "network")}
                  {sortHeader("PRODUCT", "product_canonical")}
                  {sortHeader("LOCATION", "station_name")}
                  {sortHeader("EFF DATE", "effective_date")}
                  {sortHeader("MARGIN", "margin_cpl", "text-right")}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((m) => {
                  const shadowedBy = computeShadow(m);
                  const isDefd = m.product_canonical === "DEFD";
                  // USA margins are stored numerically as cents-per-unit
                  // but the unit is GALLONS, not litres. Display the
                  // correct suffix so the operator never sees "¢/L" on a
                  // USA TA Petro rule (May 2026 spec).
                  const isUsa = (m.country || "CA").toUpperCase() === "US";
                  // DEFD-specific display rules (May 2026 operator spec):
                  //   ESSO DEFD (replace mode)     → "$/L" (final price)
                  //   Pipeline DEFD (add mode)     → "¢/L" (margin)
                  //   TA Petro DEFD (USA, add)     → "¢/Gal" (margin)
                  //   Diesel family                → "¢/L" (CA) or "¢/Gal" (USA)
                  const isReplaceMode = (m.pricing_mode || "add") === "replace";
                  const displayDefdAsDollars = isDefd && isReplaceMode;
                  const displayVal = displayDefdAsDollars
                    ? (m.margin_cpl / 100).toFixed(2)
                    : m.margin_cpl.toFixed(2);
                  const displayUnit = displayDefdAsDollars
                    ? (isUsa ? "$/Gal" : "$/L")
                    : (isUsa ? "¢/Gal" : "¢/L");
                  // Prefer station_name (returned by enriched GET /margins) over raw site number.
                  const locationLabel = m.scope === "station"
                    ? (m.station_name ? `${m.station_name} (#${m.station_id})` : `#${m.station_id || "—"}`)
                    : (m.city || m.province || "—");
                  const checked = selectedRuleIds.includes(m.margin_id);
                  return (
                  <tr key={m.margin_id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          setSelectedRuleIds((prev) =>
                            e.target.checked
                              ? [...prev, m.margin_id]
                              : prev.filter((id) => id !== m.margin_id)
                          );
                        }}
                        data-testid={`margin-select-${m.margin_id}`}
                      />
                    </td>
                    <td className="font-mono text-xs" data-testid={`margin-country-${m.margin_id}`}>{(m.country || "").toUpperCase() || "—"}</td>
                    <td className="font-mono text-xs">{m.scope.toUpperCase()}</td>
                    <td className="font-mono">{m.network.toUpperCase()}</td>
                    <td className="font-mono text-xs">{m.product_canonical}</td>
                    <td className="text-xs">
                      {locationLabel}
                      {shadowedBy && (
                        <span
                          className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono bg-amber-100 text-amber-900 border border-amber-300"
                          title={`City ${shadowedBy.city} has a ${shadowedBy.margin_cpl} cpl rule effective ${shadowedBy.effective_date} for ${shadowedBy.network.toUpperCase()} ${shadowedBy.product_canonical}. This older station rule overrides it.`}
                          data-testid={`shadow-badge-${m.margin_id}`}
                        >
                          ⚠ Shadows city rule
                        </span>
                      )}
                    </td>
                    <td className="font-mono text-xs">{m.effective_date}</td>
                    <td className="gf-num">
                      {inlineEdit?.id === m.margin_id ? (
                        <input
                          type="number"
                          step="0.01"
                          autoFocus
                          className="gf-input"
                          style={{ width: 90, textAlign: "right", display: "inline-block" }}
                          value={inlineEdit.value}
                          onChange={(e) => setInlineEdit({ id: m.margin_id, value: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveInlineMargin(m, inlineEdit.value);
                            else if (e.key === "Escape") setInlineEdit(null);
                          }}
                          onBlur={() => saveInlineMargin(m, inlineEdit.value)}
                          data-testid={`inline-margin-input-${m.margin_id}`}
                        />
                      ) : (
                        <span
                          onClick={() => setInlineEdit({ id: m.margin_id, value: displayVal })}
                          style={{ cursor: "pointer", borderBottom: "1px dashed #a1a1aa" }}
                          title="Click to edit · Enter to save · Esc to cancel"
                          data-testid={`inline-margin-trigger-${m.margin_id}`}
                        >
                          {displayVal}
                        </span>
                      )}{" "}
                      <span className="text-xs text-zinc-500">{displayUnit}</span>
                    </td>
                    <td className="text-right">
                      {shadowedBy && (
                        <button
                          className="gf-btn gf-btn-ghost"
                          onClick={async () => {
                            if (!window.confirm(
                              `Delete this station rule (${m.station_name || `#${m.station_id}`} · ${m.margin_cpl} cpl) so the city rule for ${shadowedBy.city} (${shadowedBy.margin_cpl} cpl) takes over?`
                            )) return;
                            try {
                              await api.delete(`/margins/${m.margin_id}`);
                              toast.success("Station rule removed · city rule now applies");
                              load();
                            } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                          }}
                          data-testid={`use-city-rule-${m.margin_id}`}
                          title={`Delete this station rule so the ${shadowedBy.city} city rule applies`}
                          style={{ color: "#92400e" }}
                        >
                          Use city rule
                        </button>
                      )}
                      <button className="gf-btn gf-btn-ghost" onClick={() => copyRule(m)}
                        data-testid={`copy-margin-${m.margin_id}`} title="Copy to next date">
                        <Copy size={14}/>
                      </button>
                      <button className="gf-btn gf-btn-ghost" onClick={() => editMargin(m)}
                        data-testid={`edit-margin-${m.margin_id}`} title="Edit rule">
                        <PencilSimple size={14}/>
                      </button>
                      <button className="gf-btn gf-btn-ghost" onClick={() => delMargin(m.margin_id)}
                        data-testid={`delete-margin-${m.margin_id}`} title="Delete rule">
                        <Trash size={14}/>
                      </button>
                    </td>
                  </tr>);
                })}
                {margins.length === 0 && <tr><td colSpan={9} className="text-center text-zinc-400 py-6">No rules yet.</td></tr>}
              </tbody>
            </table>
              );
            })()}
          </div>
        </div>

        <MarginRuleHistory customerId={customerId} />

        <div className="gf-card">
          <div className="gf-card-header">
            <h3>Fuel cards</h3>
            <div className="flex items-center gap-2">
              <div className="overline">{cards.length}</div>
              <button className="gf-btn gf-btn-outline" onClick={() => setShowBulkCards(true)}
                data-testid="bulk-add-cards-button">Bulk Add Cards</button>
              <button className="gf-btn gf-btn-outline" onClick={() => setEditingCard({ network: "esso", card_number: "", status: "active", notes: "", unit: "", truck_id: "", driver_id: "", phone: "" })}
                data-testid="add-card-button"><Plus size={12}/> Add card</button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="gf-table">
              <thead><tr><th>CARD</th><th>NETWORK</th><th>NOTES</th><th>STATUS</th><th></th></tr></thead>
              <tbody>
                {cards.map((c) => (
                  <tr key={c.card_id}>
                    <td className="font-mono">{formatCard(c.card_number, c.network, c.last4)}</td>
                    <td className="font-mono text-xs">{c.network.toUpperCase()}</td>
                    <td className="text-xs">{c.notes}</td>
                    <td><Badge variant={c.status === "active" ? "success" : "muted"}>{c.status}</Badge></td>
                    <td className="text-right">
                      <button className="gf-btn gf-btn-ghost" onClick={() => setEditingCard({ ...c })}
                        title="Edit" data-testid={`edit-card-${c.card_id}`}><PencilSimple size={14}/></button>
                      <button className="gf-btn gf-btn-ghost" onClick={() => deleteCard(c)}
                        title="Delete" data-testid={`delete-card-${c.card_id}`}><Trash size={14}/></button>
                    </td>
                  </tr>
                ))}
                {cards.length === 0 && <tr><td colSpan={5} className="text-center text-zinc-400 py-6">No cards. Click <b>Add card</b> to assign one.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <div className="gf-card">
          <div className="gf-card-header"><h3>Recent transactions</h3><Link to={`/transactions?customer=${customerId}`} className="overline hover:underline">VIEW ALL →</Link></div>
          <div className="px-3 pt-2 pb-3 border-b border-zinc-100">
            {/* Transaction-report download (May 2026 operator spec).
                CA / USA / BOTH — same backend endpoint, different
                `country` query param. Defaults to last 30 days; admin
                can override the dates in the URL or via the central
                Transactions page if a custom window is needed.
                Filename comes from the backend so it matches the
                operator's "Transaction Report_<Customer>_<NET>_..." spec. */}
            <div className="overline mb-1">Transaction reports</div>
            <div className="flex flex-wrap gap-2">
              {[
                { label: "BOTH (CA + USA)", country: null, testid: "tx-report-both" },
                { label: "Canada only", country: "CA", testid: "tx-report-ca" },
                { label: "USA (Esso USD/GAL) only", country: "US", testid: "tx-report-us" },
              ].map(({ label, country, testid }) => {
                const today = new Date().toISOString().slice(0, 10);
                const from = new Date(Date.now() - 30 * 86400_000).toISOString().slice(0, 10);
                const baseParams = {
                  date_from: from, date_to: today, customer_id: customerId,
                  for_customer: "true",
                  ...(country ? { country } : {}),
                };
                return (
                  <div key={testid} className="flex items-center gap-1">
                    <span className="text-xs text-zinc-500">{label}</span>
                    <button
                      className="gf-btn gf-btn-outline gf-btn-xs"
                      data-testid={`${testid}-csv`}
                      onClick={() => downloadFromBackend("/transactions/report/csv", baseParams).then(
                        (fn) => toast.success(`Downloaded ${fn}`),
                        (e) => toast.error(e.response?.data?.detail || "Download failed"),
                      )}
                    >CSV</button>
                    <button
                      className="gf-btn gf-btn-outline gf-btn-xs"
                      data-testid={`${testid}-pdf`}
                      onClick={() => downloadFromBackend("/transactions/report/pdf", baseParams).then(
                        (fn) => toast.success(`Downloaded ${fn}`),
                        (e) => toast.error(e.response?.data?.detail || "Download failed"),
                      )}
                    >PDF</button>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="overflow-x-auto" style={{ maxHeight: 360 }}>
            <table className="gf-table">
              <thead><tr><th>DATE</th><th>CARD</th><th>STATION</th><th>PRODUCT</th><th className="text-right">QTY</th><th className="text-right">AMT</th></tr></thead>
              <tbody>
                {txs.slice(0, 12).map((t) => (
                  <tr key={t.transaction_id}>
                    <td className="font-mono text-xs">{t.tran_date}</td>
                    <td className="font-mono">{formatCard(t.card_number, t.network, t.card_last4)}</td>
                    <td className="text-xs">{t.station_name}</td>
                    <td className="font-mono text-xs">{displayProduct(t.product, t.network)}</td>
                    <td className="gf-num">{t.qty.toFixed(1)}</td>
                    <td className="gf-num">${t.customer_amount?.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="gf-card">
          <div className="gf-card-header"><h3>Invoices</h3><Link to="/invoices" className="overline hover:underline">ALL →</Link></div>
          <div className="overflow-x-auto">
            <table className="gf-table">
              <thead><tr><th>#</th><th>ISSUED</th><th>STATUS</th><th className="text-right">TOTAL</th></tr></thead>
              <tbody>
                {invoices.map((i) => (
                  <tr key={i.invoice_id}>
                    <td><Link to={`/invoices/${i.invoice_id}`} className="font-mono text-xs hover:underline">{i.invoice_number}</Link></td>
                    <td className="font-mono text-xs">{i.issue_date}</td>
                    <td><Badge variant={i.status === "paid" ? "success" : i.status === "overdue" ? "error" : "info"}>{i.status}</Badge></td>
                    <td className="gf-num">${i.total?.toFixed(2)}</td>
                  </tr>
                ))}
                {invoices.length === 0 && <tr><td colSpan={4} className="text-center text-zinc-400 py-6">No invoices.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {editingCard && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditingCard(null)}>
          <div className="bg-white w-full max-w-md border border-zinc-200" onClick={(e) => e.stopPropagation()}>
            <div className="gf-card-header"><h3>{editingCard.card_id ? "Edit card" : "Add fuel card"}</h3></div>
            <FuelCardForm
              value={editingCard}
              onChange={(patch) => setEditingCard({ ...editingCard, ...patch })}
              isEdit={!!editingCard.card_id}
              testIdPrefix="customer-fuel-card"
              excludeCardId={editingCard.card_id || null}
            />
            <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
              <button className="gf-btn gf-btn-outline" onClick={() => setEditingCard(null)}>Cancel</button>
              <button className="gf-btn gf-btn-primary" onClick={saveCard} data-testid="save-card-button">
                {editingCard.card_id ? "Save changes" : "Add card"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showBulkCards && (
        <BulkCardUploadModal
          customerId={customerId}
          companyName={customer?.company_name || ""}
          onClose={() => setShowBulkCards(false)}
          onCommitted={() => { load(); setShowBulkCards(false); }}
        />
      )}

      {showCopyPrompt && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={() => setShowCopyPrompt(false)}>
          <div className="bg-white w-full max-w-sm border border-zinc-200" onClick={(e) => e.stopPropagation()}>
            <div className="gf-card-header"><h3>Copy {selectedRuleIds.length} rule{selectedRuleIds.length === 1 ? "" : "s"}</h3></div>
            <div className="gf-card-body space-y-3">
              <div className="text-xs text-zinc-500">
                Each selected rule will be duplicated with the new effective date below. Source rules are kept as-is.
              </div>
              <div>
                <div className="overline mb-1">Target effective date</div>
                <input
                  type="date"
                  className="gf-input"
                  value={copyToDate}
                  onChange={(e) => setCopyToDate(e.target.value)}
                  data-testid="margin-bulk-copy-date"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
              <button className="gf-btn gf-btn-outline" onClick={() => setShowCopyPrompt(false)}>Cancel</button>
              <button className="gf-btn gf-btn-primary" onClick={bulkCopyRules} data-testid="margin-bulk-copy-confirm">
                Copy
              </button>
            </div>
          </div>
        </div>
      )}

      {showMargin && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => { setShowMargin(false); setEditingMarginId(null); setSelectedCities([]); setCityFilter(""); setLastCityClickIdx(null); setSelectedStations([]); setStationFilter(""); setLastStationClickIdx(null); }}>          <div className="bg-white w-full max-w-md border border-zinc-200 max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="gf-card-header"><h3>{editingMarginId ? "Edit margin rule" : "New margin rule"}</h3></div>
            <div className="gf-card-body space-y-3 overflow-y-auto flex-1">
              <div><div className="overline mb-1">Country</div>
                <select className="gf-input" value={mForm.country || "CA"}
                  onChange={(e) => {
                    const country = e.target.value;
                    // Switching to US resets scope to country_default and
                    // clears CA-only city/station picks. Network is left
                    // as-picked so operators can now choose Petro-Pass USA
                    // (Phase 3E-1); we only reset when the previous value
                    // is invalid for the new country (e.g. petro_pass → CA
                    // demotes to ANY since Petro-Pass CA uses the CA path).
                    const prevNet = (mForm.network || "ANY");
                    const usValid = new Set(["ANY", "petro_pass"]);
                    const caValid = new Set(["ANY", "esso", "pipeline"]);
                    const nextNet = country === "US"
                      ? (usValid.has(prevNet) ? prevNet : "ANY")
                      : (caValid.has(prevNet) ? prevNet : "ANY");
                    setMForm({
                      ...mForm,
                      country,
                      scope: country === "US" ? "country_default" : "default",
                      station_id: "", station_name: "", city: "", province: "",
                      network: nextNet,
                    });
                    setSelectedCities([]);
                    setLastCityClickIdx(null);
                    setCityFilter("");
                    setSelectedStations([]);
                    setLastStationClickIdx(null);
                    setStationFilter("");
                  }}
                  data-testid="margin-country">
                  <option value="CA">Canada</option>
                  <option value="US">USA (Esso USD/GAL · Petro-Pass USA · EFS)</option>
                </select>
              </div>

              <div><div className="overline mb-1">Scope</div>
                <select className="gf-input" value={mForm.scope}
                  onChange={(e) => {
                    setMForm({ ...mForm, scope: e.target.value, station_id: "", station_name: "", city: "", province: "" });
                    setSelectedCities([]);
                    setLastCityClickIdx(null);
                    setCityFilter("");
                    setSelectedStations([]);
                    setLastStationClickIdx(null);
                    setStationFilter("");
                  }}
                  data-testid="margin-scope">
                  {(mForm.country || "CA") === "US" ? (
                    <>
                      <option value="country_default">All USA (default)</option>
                      <option value="province">State</option>
                      <option value="city">City</option>
                      <option value="station_name">Station name</option>
                      <option value="station">Station ID</option>
                    </>
                  ) : (
                    <>
                      <option value="default">Default (all stations)</option>
                      <option value="province">Province</option>
                      <option value="city">City</option>
                      <option value="station">Specific station</option>
                    </>
                  )}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div><div className="overline mb-1">Network</div>
                  <select className="gf-input" value={mForm.network}
                    data-testid="margin-network"
                    onChange={(e) => {
                      const nw = e.target.value;
                      // Default product per network
                      let prod = mForm.product_canonical;
                      const allowed = productsForNetwork(nw);
                      if (!allowed.some((p) => p.value === prod)) prod = allowed[0].value;
                      setMForm({ ...mForm, network: nw, product_canonical: prod, station_id: "" });
                    }}>
                    <option value="ANY">ANY</option>
                    {(mForm.country || "CA") === "US" ? (
                      <>
                        <option value="petro_pass">Petro-Pass USA USD/GAL</option>
                        <option value="ta_petro">Esso USD/GAL</option>
                      </>
                    ) : (
                      <>
                        <option value="esso">Esso CAD/L</option>
                        <option value="pipeline">Pipeline CAD/L</option>
                        <option value="petro_pass">Petro-Pass Canada CAD/L</option>
                      </>
                    )}
                  </select>
                </div>
                <div><div className="overline mb-1">Product</div>
                  <select className="gf-input" value={mForm.product_canonical}
                    onChange={(e) => setMForm({ ...mForm, product_canonical: e.target.value })}>
                    {productsForNetwork(mForm.network).map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {mForm.scope === "province" && (
                <div><div className="overline mb-1">{(mForm.country || "CA") === "US" ? "State" : "Province"}</div>
                  <select className="gf-input" value={mForm.province}
                    onChange={(e) => setMForm({ ...mForm, province: e.target.value })} data-testid="margin-province-select">
                    <option value="">Select {(mForm.country || "CA") === "US" ? "state" : "province"}…</option>
                    {((mForm.country || "CA") === "US"
                      ? ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"]
                      : ["AB","BC","MB","NB","NL","NS","NT","NU","ON","PE","QC","SK","YT"]
                    ).map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
              )}

              {mForm.scope === "station_name" && (
                <div><div className="overline mb-1">Station name (USA)</div>
                  <input className="gf-input" value={mForm.station_name || ""}
                    onChange={(e) => setMForm({ ...mForm, station_name: e.target.value.toUpperCase() })}
                    placeholder="e.g. TA HUDSON, TA EXPRESS GRAND FORKS"
                    data-testid="margin-station-name-input"
                  />
                  <div className="text-[11px] text-zinc-500 mt-1">Exact match against the station name in the transaction file.</div>
                </div>
              )}

              {mForm.scope === "city" && (() => {
                const visible = cities.filter((c) =>
                  !cityFilter || c.toLowerCase().includes(cityFilter.toLowerCase())
                );
                const selectedSet = new Set(selectedCities);
                const onCityClick = (city, idx, e) => {
                  // Shift-click → range select from last anchor on the visible list.
                  if (e.shiftKey && lastCityClickIdx !== null) {
                    const start = Math.min(lastCityClickIdx, idx);
                    const end = Math.max(lastCityClickIdx, idx);
                    const range = visible.slice(start, end + 1);
                    const merged = new Set(selectedCities);
                    range.forEach((c) => merged.add(c));
                    setSelectedCities(Array.from(merged));
                  } else if (e.metaKey || e.ctrlKey) {
                    // Ctrl/Cmd-click → toggle single entry, keep others.
                    const next = new Set(selectedCities);
                    if (next.has(city)) next.delete(city); else next.add(city);
                    setSelectedCities(Array.from(next));
                    setLastCityClickIdx(idx);
                  } else {
                    // Plain click → toggle (so a single click can select-then-deselect).
                    if (selectedSet.has(city) && selectedCities.length === 1) {
                      setSelectedCities([]);
                    } else {
                      setSelectedCities([city]);
                    }
                    setLastCityClickIdx(idx);
                  }
                };
                return (
                  <div data-testid="margin-city-multiselect">
                    <div className="overline mb-1 flex items-center justify-between">
                      <span>Cities {cities.length > 0 && `(${visible.length}/${cities.length})`}</span>
                      <span className="text-amber-600 normal-case tracking-normal">
                        {selectedCities.length > 0 ? `${selectedCities.length} selected` : ""}
                      </span>
                    </div>
                    <input
                      type="text"
                      className="gf-input mb-2"
                      placeholder="Filter cities…"
                      value={cityFilter}
                      onChange={(e) => setCityFilter(e.target.value)}
                      data-testid="margin-city-filter"
                    />
                    <div
                      className="border border-zinc-200 bg-white"
                      style={{ maxHeight: 220, overflowY: "auto" }}
                    >
                      {visible.length === 0 ? (
                        <div className="p-3 text-xs text-zinc-400 font-mono">No matches.</div>
                      ) : (
                        visible.map((c, idx) => {
                          const sel = selectedSet.has(c);
                          return (
                            <div
                              key={c}
                              onClick={(e) => onCityClick(c, idx, e)}
                              data-testid={`margin-city-row-${c}`}
                              data-selected={sel ? "true" : "false"}
                              style={{ userSelect: "none", cursor: "pointer" }}
                              className={`px-3 py-1.5 text-xs font-mono border-b border-zinc-100 flex items-center gap-2 ${sel ? "bg-zinc-900 text-white" : "hover:bg-zinc-50"}`}
                            >
                              <span
                                className={`inline-block w-3 h-3 border ${sel ? "bg-amber-400 border-amber-400" : "border-zinc-400"}`}
                              />
                              <span className="flex-1">{c}</span>
                            </div>
                          );
                        })
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="text-xs text-zinc-500">
                        Click to select. Shift-click for range. Ctrl/Cmd-click to toggle.
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          className="gf-btn gf-btn-ghost text-xs"
                          onClick={() => setSelectedCities(visible)}
                          data-testid="margin-city-select-all"
                        >Select visible</button>
                        <button
                          type="button"
                          className="gf-btn gf-btn-ghost text-xs"
                          onClick={() => { setSelectedCities([]); setLastCityClickIdx(null); }}
                          data-testid="margin-city-clear"
                        >Clear</button>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {mForm.scope === "station" && (() => {
                const visible = stations.filter((s) => {
                  if (!stationFilter) return true;
                  const hay = `${s.station_id} ${s.station_name} ${s.city || ""} ${s.province || ""}`.toLowerCase();
                  return hay.includes(stationFilter.toLowerCase());
                });
                const selectedSet = new Set(selectedStations);
                const onStationClick = (sid, idx, e) => {
                  if (e.shiftKey && lastStationClickIdx !== null) {
                    const start = Math.min(lastStationClickIdx, idx);
                    const end = Math.max(lastStationClickIdx, idx);
                    const range = visible.slice(start, end + 1).map((s) => s.station_id);
                    const merged = new Set(selectedStations);
                    range.forEach((s) => merged.add(s));
                    setSelectedStations(Array.from(merged));
                  } else if (e.metaKey || e.ctrlKey) {
                    const next = new Set(selectedStations);
                    if (next.has(sid)) next.delete(sid); else next.add(sid);
                    setSelectedStations(Array.from(next));
                    setLastStationClickIdx(idx);
                  } else {
                    if (selectedSet.has(sid) && selectedStations.length === 1) {
                      setSelectedStations([]);
                    } else {
                      setSelectedStations([sid]);
                    }
                    setLastStationClickIdx(idx);
                  }
                };
                return (
                  <div data-testid="margin-station-multiselect">
                    <div className="overline mb-1 flex items-center justify-between">
                      <span>Stations {stations.length > 0 && `(${visible.length}/${stations.length})`}</span>
                      <span className="text-amber-600 normal-case tracking-normal">
                        {selectedStations.length > 0 ? `${selectedStations.length} selected` : ""}
                      </span>
                    </div>
                    <input
                      type="text"
                      className="gf-input mb-2"
                      placeholder="Filter by id, name, city, province…"
                      value={stationFilter}
                      onChange={(e) => setStationFilter(e.target.value)}
                      data-testid="margin-station-filter"
                    />
                    <div
                      className="border border-zinc-200 bg-white"
                      style={{ maxHeight: 220, overflowY: "auto" }}
                    >
                      {visible.length === 0 ? (
                        <div className="p-3 text-xs text-zinc-400 font-mono">No matches.</div>
                      ) : (
                        visible.map((s, idx) => {
                          const sel = selectedSet.has(s.station_id);
                          return (
                            <div
                              key={`${s.network}-${s.station_id}`}
                              onClick={(e) => onStationClick(s.station_id, idx, e)}
                              data-testid={`margin-station-row-${s.station_id}`}
                              data-selected={sel ? "true" : "false"}
                              style={{ userSelect: "none", cursor: "pointer" }}
                              className={`px-3 py-1.5 text-xs font-mono border-b border-zinc-100 flex items-center gap-2 ${sel ? "bg-zinc-900 text-white" : "hover:bg-zinc-50"}`}
                            >
                              <span
                                className={`inline-block w-3 h-3 border ${sel ? "bg-amber-400 border-amber-400" : "border-zinc-400"}`}
                              />
                              <span className="flex-1 truncate">
                                {s.network.toUpperCase()} · #{s.station_id} · {s.station_name} · {s.province}
                              </span>
                            </div>
                          );
                        })
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="text-xs text-zinc-500">
                        Click to select. Shift-click for range. Ctrl/Cmd-click to toggle.
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          className="gf-btn gf-btn-ghost text-xs"
                          onClick={() => setSelectedStations(visible.map((s) => s.station_id))}
                          data-testid="margin-station-select-all"
                        >Select visible</button>
                        <button
                          type="button"
                          className="gf-btn gf-btn-ghost text-xs"
                          onClick={() => { setSelectedStations([]); setLastStationClickIdx(null); }}
                          data-testid="margin-station-clear"
                        >Clear</button>
                      </div>
                    </div>
                  </div>
                );
              })()}

              <div>
                {(() => {
                  // DEFD pricing semantics by network (May 2026 operator spec):
                  //
                  //   • ESSO DEFD       → pricing_mode='replace', stored as ¢/L
                  //     (admin enters $/L, multiplied by 100 on save).
                  //     Customer Unit Price = entered $/L (final selling price).
                  //
                  //   • Pipeline DEFD   → pricing_mode='add', stored as ¢/L.
                  //     Customer Unit Price = supplier $/L + (margin ¢/L / 100).
                  //
                  //   • TA Petro DEFD   → pricing_mode='add', stored as ¢/Gal.
                  //     Customer Unit Price = supplier $/Gal + (margin ¢/Gal / 100).
                  //
                  // Diesel-family margins keep their existing ¢/L (or ¢/Gal
                  // for USA) `add`-mode semantics — unchanged.
                  const isDefd = mForm.product_canonical === "DEFD";
                  const isUsa = (mForm.country || "CA").toUpperCase() === "US";
                  const isEssoDefd = isDefd && !isUsa && mForm.network === "esso";
                  const isPipelineDefd = isDefd && !isUsa && mForm.network === "pipeline";
                  const isTaPetroDefd = isDefd && isUsa;

                  let label, helper, isDollarInput;
                  if (isEssoDefd) {
                    label = "ESSO DEFD Final Price ($/L)";
                    helper = `Direct final selling price per litre. Enter 1.40 for $1.40/L. Stored as ${mForm.margin_cpl.toFixed(2)} ¢/L (replace mode).`;
                    isDollarInput = true;
                  } else if (isPipelineDefd) {
                    label = "Pipeline DEFD Margin (¢/L)";
                    helper = "Margin in cents per litre added on top of supplier price. Enter 15 for 15 ¢/L.";
                    isDollarInput = false;
                  } else if (isTaPetroDefd) {
                    // Network-neutral USA DEFD margin label. The same
                    // USA DEFD margin field is used across supported
                    // USA networks (Esso, Petro-Pass, etc.). Payload
                    // key `margin_cpl`, unit storage, and calculation
                    // are unchanged — display label only.
                    label = "USA DEFD Margin (¢/Gallon)";
                    helper = "Margin in cents per US gallon added on top of supplier price. Enter 25 for 25 ¢/Gal.";
                    isDollarInput = false;
                  } else if (isDefd) {
                    // DEFD with network=ANY — fallback uses ¢/L margin (add mode)
                    label = "DEFD Margin (¢/L)";
                    helper = "Margin in cents per litre.";
                    isDollarInput = false;
                  } else {
                    // Diesel family — unchanged ¢/L (CA) or ¢/Gal (USA).
                    label = `Margin (${isUsa ? "¢/Gallon" : "¢/L"})`;
                    helper = `Example: enter 5.0 for 5 cents/${isUsa ? "gallon" : "litre"}.`;
                    isDollarInput = false;
                  }
                  return (
                    <>
                      <div className="overline mb-1">{label}</div>
                      <input
                        type="number" step="0.01" className="gf-input"
                        value={marginInput}
                        onChange={(e) => {
                          setMarginInput(e.target.value);
                          const n = parseFloat(e.target.value) || 0;
                          const cpl = isDollarInput ? n * 100 : n;
                          // Auto-pick pricing_mode based on the DEFD network rule above.
                          // Diesel-family rules and ANY-network DEFD keep their
                          // current pricing_mode (do not flip it under the user).
                          let nextMode = mForm.pricing_mode;
                          if (isEssoDefd) nextMode = "replace";
                          else if (isPipelineDefd || isTaPetroDefd) nextMode = "add";
                          setMForm({ ...mForm, margin_cpl: cpl, pricing_mode: nextMode });
                        }}
                        data-testid="margin-cpl-input"
                      />
                      <div className="text-xs text-zinc-500 mt-1">{helper}</div>
                    </>
                  );
                })()}
              </div>

              <div>
                <div className="overline mb-1">Effective date</div>
                <input type="date" className="gf-input" value={mForm.effective_date}
                  onChange={(e) => setMForm({ ...mForm, effective_date: e.target.value })} />
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
              <button className="gf-btn gf-btn-outline" onClick={() => { setShowMargin(false); setEditingMarginId(null); setSelectedCities([]); setCityFilter(""); setLastCityClickIdx(null); setSelectedStations([]); setStationFilter(""); setLastStationClickIdx(null); }}>Cancel</button>
              <button className="gf-btn gf-btn-primary" onClick={addMargin} data-testid="save-margin-button">
                {editingMarginId
                  ? "Save changes"
                  : mForm.scope === "city" && selectedCities.length > 1
                    ? `Save ${selectedCities.length} rules`
                    : mForm.scope === "station" && selectedStations.length > 1
                      ? `Save ${selectedStations.length} rules`
                      : "Save rule"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPrefs && (
        <PrefsModal
          customer={customer}
          onClose={() => setShowPrefs(false)}
          onSaved={(updated) => { setCustomer(updated); setShowPrefs(false); }}
        />
      )}

      {showDelete && (
        <DeleteCustomerModal
          customer={customer}
          onClose={() => setShowDelete(false)}
          onDeleted={() => { toast.success(`${customer.company_name} deleted`); window.location.href = "/customers"; }}
        />
      )}

      {showEmailQuick && (
        <QuickEmailReportModal
          kind={showEmailQuick}
          customer={customer}
          customerId={customerId}
          onClose={() => setShowEmailQuick(null)}
        />
      )}

      {showReport && (
        <ReportModal
          customer={customer}
          customerId={customerId}
          kind={showReport}
          dateFrom={reportFrom}
          dateTo={reportTo}
          setDateFrom={setReportFrom}
          setDateTo={setReportTo}
          network={reportNetwork}
          setNetwork={setReportNetwork}
          rows={reportRows}
          missingDates={reportMissingDates}
          conflicts={reportConflicts}
          taxSummary={reportTaxSummary}
          onConflictsChanged={(c) => setReportConflicts(c)}
          loading={reportLoading}
          onClose={() => { setShowReport(null); setReportRows(null); setReportMissingDates([]); setReportConflicts([]); setReportTaxSummary([]); }}
          onFetch={async () => {
            setReportLoading(true);
            try {
              const params = { date_from: reportFrom, date_to: reportTo };
              // Phase 3E-9d — reportNetwork is a compound-safe value:
              //   "both"           → legacy Esso+Pipeline (no filter)
              //   "esso" | "pipeline"
              //   "petro_pass_ca"  → {network:petro_pass, country:CA}
              //   "petro_pass_us"  → {network:petro_pass, country:US}
              if (showReport === "price" && reportNetwork && reportNetwork !== "both") {
                if (reportNetwork === "petro_pass_ca") {
                  params.network = "petro_pass"; params.country = "CA";
                } else if (reportNetwork === "petro_pass_us") {
                  params.network = "petro_pass"; params.country = "US";
                } else {
                  params.network = reportNetwork;
                }
              }
              const { data } = await api.get(`/customers/${customerId}/${showReport}-report`, { params });
              setReportRows(data.rows || []);
              setReportMissingDates(data.missing_dates || []);
              setReportConflicts(data.conflicts || []);
              setReportTaxSummary(data.tax_summary || []);
            } catch (e) {
              toast.error(e.response?.data?.detail || "Failed to build report");
            } finally { setReportLoading(false); }
          }}
        />
      )}

      {marginUpload && (
        <MarginUploadModal
          state={marginUpload}
          setState={setMarginUpload}
          onCommitted={() => { setMarginUpload(null); load(); }}
          contextLabel={customer.company_name?.toUpperCase()}
        />
      )}

      {showShadowConfirm && (
        <ShadowResolveModal
          shadowedRules={shadowedRules}
          onClose={() => setShowShadowConfirm(false)}
          onResolved={() => { setShowShadowConfirm(false); load(); }}
        />
      )}

      {/* Phase 2 (2026-02) — Portal Users tab for this customer. */}
      <PortalUsersSection customerId={customerId} customerName={customer.company_name} />

      {/* Phase 3E-9Q.2 — backdated pricing-change guard for manual margin ops. */}
      <BackdatedConfirmModal
        open={!!backdated?.open}
        onCancel={() => setBackdated(null)}
        onConfirm={() => backdated?.retry && backdated.retry()}
        effectiveDate={backdated?.effectiveDate}
        contextLabel={backdated?.contextLabel}
        customerCount={backdated?.customerCount}
        ruleCount={backdated?.ruleCount}
        busy={!!backdated?.busy}
      />
    </SectionPad>
  );
}



// ---------- PREFERENCES MODAL ----------
const BILLING_DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const PROVINCES = ["AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "ON", "PE", "QC", "SK", "YT"];

function PrefsModal({ customer, onClose, onSaved }) {
  const [f, setF] = React.useState({
    ...customer,
    billing_days: customer.billing_days || ["mon"],
    price_report_provinces: customer.price_report_provinces || [],
    // Per-network province lists. `null` (or undefined) means "fall back to legacy
    // price_report_provinces"; an empty array means "all provinces for this network".
    price_report_provinces_esso: customer.price_report_provinces_esso ?? null,
    price_report_provinces_pipeline: customer.price_report_provinces_pipeline ?? null,
    price_report_networks: customer.price_report_networks || ["esso", "pipeline"],
    customer_type: customer.customer_type || "postpaid",
    postpaid_credit_limit: customer.postpaid_credit_limit ?? customer.credit_limit ?? 0,
    prepaid_balance_limit: customer.prepaid_balance_limit ?? 0,
    usd_invoice_currency: customer.usd_invoice_currency || "CAD",
    usd_to_cad_rate: customer.usd_to_cad_rate ?? 1.37,
    invoice_send_mode: customer.invoice_send_mode || "manual",
    invoice_email_enabled: customer.invoice_email_enabled ?? true,
    auto_email_price_report: customer.auto_email_price_report ?? false,
    // P4-LEGACY-AUTO-EMAIL-TRANSACTION-REPORT-ONLY RETIREMENT R1
    // (Feb 2026) — `auto_email_transaction_report` is retired. The
    // form no longer initialises or edits it; existing customer
    // documents that still carry the stale flag are ignored server-
    // side (Price Report auto-email + Invoice auto-email + Portal
    // Transaction Report Scheduler are ALL unchanged).
    tax_id: customer.tax_id || "",
    price_report_show_gst: customer.price_report_show_gst ?? true,
    price_report_show_pst: customer.price_report_show_pst ?? true,
    price_report_show_total: customer.price_report_show_total ?? true,
    default_def_margin_cpl: customer.default_def_margin_cpl ?? 0,
  });

  const toggleIn = (key, val) => {
    const cur = f[key] || [];
    const next = cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val];
    setF({ ...f, [key]: next });
  };

  // Toggle a province in a per-network list. The first toggle of a network with
  // `null` value seeds it from the legacy `price_report_provinces` list, so the
  // user doesn't lose their existing selection when switching to per-network mode.
  const togglePerNetworkProvince = (network, prov) => {
    const key = network === "esso" ? "price_report_provinces_esso" : "price_report_provinces_pipeline";
    const seed = f[key] === null || f[key] === undefined ? [...(f.price_report_provinces || [])] : [...f[key]];
    const next = seed.includes(prov) ? seed.filter((x) => x !== prov) : [...seed, prov];
    setF({ ...f, [key]: next });
  };

  const save = async () => {
    try {
      const { data } = await api.put(`/customers/${customer.customer_id}`, f);
      toast.success("Preferences saved");
      onSaved(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-3xl border border-zinc-200 max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="gf-card-header"><h3>Edit customer — {customer.company_name}</h3></div>
        <div className="gf-card-body overflow-y-auto flex-1 space-y-5">

          {/* PROFILE — company / contact / address. All fields editable. */}
          <div>
            <div className="overline mb-2">PROFILE</div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <div className="overline mb-1">COMPANY NAME</div>
                <input className="gf-input" value={f.company_name || ""} onChange={(e) => setF({ ...f, company_name: e.target.value })} data-testid="edit-company-name" />
              </div>
              <div>
                <div className="overline mb-1">CONTACT NAME</div>
                <input className="gf-input" value={f.contact_name || ""} onChange={(e) => setF({ ...f, contact_name: e.target.value })} data-testid="edit-contact-name" />
              </div>
              <div>
                <div className="overline mb-1">CONTACT EMAIL</div>
                <input type="email" className="gf-input" value={f.contact_email || ""} onChange={(e) => setF({ ...f, contact_email: e.target.value })} data-testid="edit-contact-email" />
              </div>
              <div>
                <div className="overline mb-1">PHONE</div>
                <input className="gf-input" value={f.phone || ""} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="edit-phone" />
              </div>
              <div>
                <div className="overline mb-1">BILLING EMAIL</div>
                <input type="email" className="gf-input" value={f.billing_email || ""} onChange={(e) => setF({ ...f, billing_email: e.target.value })} data-testid="edit-billing-email" />
              </div>
              <div className="col-span-2">
                <div className="overline mb-1">STREET ADDRESS</div>
                <input className="gf-input" value={f.address || ""} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid="edit-address" />
              </div>
              <div>
                <div className="overline mb-1">CITY</div>
                <input className="gf-input" value={f.city || ""} onChange={(e) => setF({ ...f, city: e.target.value })} data-testid="edit-city" />
              </div>
              <div>
                <div className="overline mb-1">PROVINCE</div>
                <select className="gf-input" value={f.province || ""} onChange={(e) => setF({ ...f, province: e.target.value })} data-testid="edit-province">
                  <option value="">—</option>
                  {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <div className="overline mb-1">POSTAL / ZIP CODE</div>
                <input className="gf-input" value={f.postal_code || ""} onChange={(e) => setF({ ...f, postal_code: e.target.value })} data-testid="edit-postal-code" />
              </div>
              <div>
                <div className="overline mb-1">COUNTRY</div>
                <input className="gf-input" value={f.country || ""} onChange={(e) => setF({ ...f, country: e.target.value })} data-testid="edit-country" placeholder="Canada" />
              </div>
              <div>
                <div className="overline mb-1">STATUS</div>
                <select className="gf-input" value={f.status || "active"} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="edit-status">
                  <option value="active">active</option>
                  <option value="inactive">inactive</option>
                </select>
              </div>
            </div>
          </div>

          <div>
            <div className="overline mb-2">CUSTOMER TYPE</div>
            <div className="flex gap-2">
              {["postpaid", "prepaid"].map((t) => (
                <button key={t} onClick={() => setF({ ...f, customer_type: t })}
                  data-testid={`type-${t}`}
                  className={`px-4 py-2 text-xs font-mono uppercase tracking-widest border ${f.customer_type === t ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200"}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="overline mb-1">POSTPAID CREDIT LIMIT (CAD)</div>
              <input type="number" className="gf-input" value={f.postpaid_credit_limit}
                     disabled={f.customer_type !== "postpaid"}
                     onChange={(e) => setF({ ...f, postpaid_credit_limit: parseFloat(e.target.value) || 0, credit_limit: parseFloat(e.target.value) || 0 })}
                     data-testid="postpaid-limit-input" />
            </div>
            <div>
              <div className="overline mb-1">PREPAID BALANCE MIN (CAD)</div>
              <input type="number" className="gf-input" value={f.prepaid_balance_limit}
                     disabled={f.customer_type !== "prepaid"}
                     onChange={(e) => setF({ ...f, prepaid_balance_limit: parseFloat(e.target.value) || 0 })}
                     data-testid="prepaid-limit-input" />
            </div>
          </div>

          <div>
            <div className="overline mb-2">BILLING DAYS (WEEKLY)</div>
            <div className="flex gap-2 flex-wrap">
              {BILLING_DAYS.map((d) => (
                <button key={d} onClick={() => toggleIn("billing_days", d)}
                  data-testid={`billing-day-${d}`}
                  className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest border ${f.billing_days.includes(d) ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200"}`}>
                  {d}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="overline mb-2">INVOICE SEND MODE</div>
              <select className="gf-input" value={f.invoice_send_mode} onChange={(e) => setF({ ...f, invoice_send_mode: e.target.value })} data-testid="invoice-send-mode">
                <option value="manual">Manual (admin generates + sends)</option>
                <option value="auto">Auto (send on billing days)</option>
              </select>
              <label className="flex items-center gap-2 mt-2 text-xs">
                <input type="checkbox" checked={f.invoice_email_enabled} onChange={(e) => setF({ ...f, invoice_email_enabled: e.target.checked })} />
                Email invoices to billing email
              </label>
              <label className="flex items-center gap-2 mt-1 text-xs" data-testid="auto-email-price-toggle-label">
                <input
                  type="checkbox"
                  checked={!!f.auto_email_price_report}
                  onChange={(e) => setF({ ...f, auto_email_price_report: e.target.checked })}
                  data-testid="auto-email-price-toggle"
                />
                <span>
                  Receive daily price list emails
                  <span className="block text-[11px] text-zinc-500">Enables inclusion in Admin-sent daily price reports.</span>
                </span>
              </label>
              {/* P4-LEGACY-AUTO-EMAIL-TRANSACTION-REPORT-ONLY RETIREMENT R1
                  (Feb 2026) — the yesterday-transactions checkbox is
                  retired. Customers manage automated transaction
                  reports themselves via the Customer Portal
                  Transaction Report Schedule (Network + Country
                  filters, canonical CSV). Price-list auto-email above
                  is UNTOUCHED. */}
              <div className="text-[10px] text-zinc-500 mt-1 pl-5">
                Customer can opt out anytime by replying — uncheck the same box here.
              </div>
            </div>
            <div>
              <div className="overline mb-2">USD TRANSACTIONS ON INVOICE</div>
              <select className="gf-input" value={f.usd_invoice_currency} onChange={(e) => setF({ ...f, usd_invoice_currency: e.target.value })} data-testid="usd-invoice-currency">
                <option value="USD">Keep in USD</option>
                <option value="CAD">Convert to CAD</option>
              </select>
              {f.usd_invoice_currency === "CAD" && (
                <div className="mt-2">
                  <div className="overline mb-1">USD → CAD FX rate</div>
                  <input
                    type="number"
                    step="0.0001"
                    min="0"
                    className="gf-input"
                    value={f.usd_to_cad_rate}
                    onChange={(e) => setF({ ...f, usd_to_cad_rate: parseFloat(e.target.value) || 0 })}
                    placeholder="e.g. 1.3700"
                    data-testid="usd-to-cad-rate"
                  />
                  <div className="text-[11px] text-zinc-500 mt-1">
                    Applied to USD subtotal at invoice render time. Update before generating an invoice that contains Esso USD/GAL / USA fills.
                  </div>
                </div>
              )}
            </div>
          </div>

          <div>
            <div className="overline mb-2">PRICE REPORT NETWORKS (SENT TO CUSTOMER)</div>
            <div className="flex gap-2">
              {["esso", "pipeline"].map((n) => (
                <button key={n} onClick={() => toggleIn("price_report_networks", n)}
                  data-testid={`net-${n}`}
                  className={`px-4 py-2 text-xs font-mono uppercase tracking-widest border ${(f.price_report_networks || []).includes(n) ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200"}`}>
                  {n}
                </button>
              ))}
            </div>
          </div>

          {/* Phase 3E-4a1 — country-aware Petro-Pass lane authorization.
              This is SEPARATE from price_report_networks (which is
              CAD/L only). The `authorized_lanes` field is
              server-authoritative — hidden dropdowns still can't
              bypass the backend gate. */}
          <PetroPassLanesToggle
            customer={customer}
            hasUsMargin={((customer && customer.__pp_us_default_cpl) != null)}
          />

          {/* Per-network province pickers — separate rows for Esso & Pipeline. */}
          {["esso", "pipeline"].map((net) => {
            const key = net === "esso" ? "price_report_provinces_esso" : "price_report_provinces_pipeline";
            const isLegacy = f[key] === null || f[key] === undefined;
            const list = isLegacy ? (f.price_report_provinces || []) : (f[key] || []);
            const enabled = (f.price_report_networks || []).includes(net);
            return (
              <div key={net} className={enabled ? "" : "opacity-50"}>
                <div className="overline mb-2 flex items-center justify-between">
                  <span>{net.toUpperCase()} PROVINCES (EMPTY = ALL)</span>
                  {!isLegacy && (
                    <button
                      type="button"
                      className="text-xs text-zinc-500 hover:text-zinc-900 normal-case tracking-normal"
                      onClick={() => setF({ ...f, [key]: null })}
                      data-testid={`reset-${net}-prov`}
                      title="Use the legacy single provinces list for this network"
                    >Reset to default</button>
                  )}
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  {PROVINCES.map((p) => (
                    <button key={p}
                      onClick={() => enabled && togglePerNetworkProvince(net, p)}
                      disabled={!enabled}
                      data-testid={`prov-${net}-${p}`}
                      className={`px-2.5 py-1 text-xs font-mono tracking-widest border ${
                        list.includes(p) ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200"
                      }`}>
                      {p}
                    </button>
                  ))}
                </div>
                <div className="text-xs text-zinc-500 mt-2">
                  Selected: {list.length === 0 ? "All provinces" : list.join(", ")}
                  {isLegacy && <span className="ml-2 text-amber-600">· using legacy combined list</span>}
                </div>
              </div>
            );
          })}

          <div>
            <div className="overline mb-2">PRICE REPORT COLUMNS (CUSTOMER VIEW)</div>
            <div className="text-xs text-zinc-500 mb-2">
              The customer's price report always shows site, station, location,
              province, product, price/L and effective date. Toggle the tax
              columns off for clients who only want the rack price.
            </div>
            <div className="flex gap-3 flex-wrap">
              {[
                { key: "price_report_show_gst", label: "GST / HST" },
                { key: "price_report_show_pst", label: "PST / QST" },
                { key: "price_report_show_total", label: "TOTAL PRICE" },
              ].map(({ key, label }) => (
                <label key={key} className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest border border-zinc-200 px-3 py-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!f[key]}
                    onChange={(e) => setF({ ...f, [key]: e.target.checked })}
                    data-testid={`pref-${key.replace(/_/g, "-")}`}
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>

          {/* DEFAULT DEF MARGIN — removed Feb 2026. DEF pricing is now
              exclusively driven by fix-rate margin_rules uploaded via the DEF
              template. This customer-level fallback was causing silent DEF
              mispricing when the field still held a legacy additive cents
              value that got reinterpreted as a replace price. */}

          <div className="grid grid-cols-3 gap-4">
            <div>
              <div className="overline mb-1 flex items-center">
                TAX RATE
                <HelpHint
                  testId="help-tax-rate"
                  text={
                    "Provincial sales-tax rate applied to invoice subtotals for this customer. " +
                    "Stored as a decimal (e.g. 0.13 = 13% Ontario HST, 0.12 = 12% BC PST+GST, 0.05 = 5% Alberta GST). " +
                    "On invoice generation: tax_amount = subtotal × tax_rate, and total = subtotal + tax_amount."
                  }
                />
              </div>
              <input type="number" step="0.01" className="gf-input" value={f.tax_rate} onChange={(e) => setF({ ...f, tax_rate: parseFloat(e.target.value) || 0 })} />
              <div className="text-xs text-zinc-500 mt-1">Decimal — e.g. 0.13 = 13% (ON HST).</div>
            </div>
            <div>
              <div className="overline mb-1 flex items-center">
                TAX ID / HST
                <HelpHint
                  testId="help-tax-id"
                  text={
                    "The customer's HST/GST registration number (or any tax identifier you want printed " +
                    "on their invoices). Optional — leave blank if you don't track this. Appears on the PDF " +
                    "invoice header next to the company name."
                  }
                />
              </div>
              <input className="gf-input" value={f.tax_id} onChange={(e) => setF({ ...f, tax_id: e.target.value })} />
              <div className="text-xs text-zinc-500 mt-1">Optional — printed on the invoice PDF.</div>
            </div>
            <div>
              <div className="overline mb-1 flex items-center">
                NON-LISTED SURCHARGE CPL
                <HelpHint
                  testId="help-non-listed-surcharge"
                  text={
                    "Extra cents-per-litre charged when the customer fuels at a station that is NOT on their " +
                    "discount/price list. Discourages drivers from fuelling at unlisted stations and protects " +
                    "your margin where rack pricing wasn't pre-negotiated. " +
                    "Example: a value of 5 means +5¢/L on top of regular pricing for non-listed transactions " +
                    "(100 L → +$5.00). Set to 0 to disable."
                  }
                />
              </div>
              <input type="number" step="0.1" className="gf-input" value={f.non_listed_surcharge_cpl} onChange={(e) => setF({ ...f, non_listed_surcharge_cpl: parseFloat(e.target.value) || 0 })} />
              <div className="text-xs text-zinc-500 mt-1">Cents/L added when fuelling at a station that's not on this customer's price list.</div>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
          <button className="gf-btn gf-btn-outline" onClick={onClose}>Cancel</button>
          <button className="gf-btn gf-btn-primary" onClick={save} data-testid="save-prefs-button">Save preferences</button>
        </div>
      </div>
    </div>
  );
}


// ---------- REPORT MODAL ----------
function ReportModal({ customer, customerId, kind, dateFrom, dateTo, setDateFrom, setDateTo, network, setNetwork, rows, missingDates = [], conflicts = [], taxSummary = [], onConflictsChanged, loading, onClose, onFetch }) {
  const isPrice = kind === "price";
  const [showEmail, setShowEmail] = React.useState(false);
  const [emailHistory, setEmailHistory] = React.useState([]);
  const loadEmailHistory = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/email/events`, { params: { customer_id: customerId, kind: "price_list", limit: 10 } });
      setEmailHistory(data || []);
    } catch (_) { /* email history is optional */ }
  }, [customerId]);
  React.useEffect(() => { if (isPrice) loadEmailHistory(); }, [isPrice, loadEmailHistory]);

  // The PDF/CSV column list mirrors the customer's prefs so the on-screen
  // preview matches what gets downloaded. Tax columns can be hidden per
  // customer (Preferences → Price report columns).
  // Phase 3E-9k — Petro-Pass USA is USD/GAL and has NO Canadian tax
  // breakdown (GST/HST/PST). Its JSON schema uses PRICE/GAL. + STATE.
  // Petro-Pass CA and legacy ESSO/Pipeline remain CAD/L with PROV. and
  // PRICE/LTR. + tax columns.
  const isPPUS = isPrice && network === "petro_pass_us";
  const cols = isPrice
    ? (isPPUS
        ? ["SITE NUMBER", "NAME", "LOCATION", "STATE", "PRODUCT",
           "PRICE/GAL.", "CURRENCY", "UOM", "EFFECTIVE DATE"]
        : [
            "SITE NUMBER", "NAME", "LOCATION", "PROV.", "PRODUCT", "PRICE/LTR.",
            ...(customer.price_report_show_gst !== false ? ["GST/HST/FNT"] : []),
            ...(customer.price_report_show_pst !== false ? ["PST/QST"] : []),
            ...(customer.price_report_show_total !== false ? ["TOTAL PRICE"] : []),
            "EFFECTIVE DATE",
          ])
    : ["SCOPE", "NETWORK", "PRODUCT", "STATION", "CITY", "PROVINCE", "MARGIN CPL", "EFFECTIVE FROM"];

  React.useEffect(() => { onFetch(); /* eslint-disable-next-line */ }, [dateFrom, dateTo, kind, network]);

  const downloadParams = { date_from: dateFrom, date_to: dateTo };
  // Phase 3E-9d — same compound-safe unpack as the on-screen fetch
  // so the CSV / PDF downloads never diverge from the preview.
  if (isPrice && network && network !== "both") {
    if (network === "petro_pass_ca") {
      downloadParams.network = "petro_pass"; downloadParams.country = "CA";
    } else if (network === "petro_pass_us") {
      downloadParams.network = "petro_pass"; downloadParams.country = "US";
    } else {
      downloadParams.network = network;
    }
  }
  const csvHref = backendDownloadUrl(`/customers/${customerId}/${kind}-report/csv`, downloadParams);
  const pdfHref = backendDownloadUrl(`/customers/${customerId}/${kind}-report/pdf`, downloadParams);
  const downloadReport = async (fmt) => {
    try {
      const filename = await downloadFromBackend(`/customers/${customerId}/${kind}-report/${fmt}`, downloadParams);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-6xl border border-zinc-200 max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="gf-card-header">
          <h3>{isPrice ? "Price report" : "Margin report"} — {customer.company_name}</h3>
          <div className="flex items-center gap-2 flex-wrap">
            {isPrice && (
              <div className="flex items-center gap-1">
                <span className="overline">NETWORK</span>
                {(() => {
                  // Phase 3E-9d — Admin preview dropdown composes options
                  // from TWO independent sources:
                  //   Legacy (CAD/L)     : customer.price_report_networks
                  //   Petro-Pass CA / US : customer.authorized_lanes
                  // Both Petro-Pass country lanes are ALWAYS visible for
                  // admins (preview parity with the JSON endpoint's admin
                  // bypass), but unauthorized lanes are visually marked
                  // "— Admin preview only · Customer not authorized" so
                  // operators never confuse preview with customer access.
                  // Backend never mutates authorized_lanes / margin /
                  // price_report_networks from a preview.
                  const legacyRaw = customer?.price_report_networks
                    || ["esso", "pipeline"];
                  const legacyLower = legacyRaw
                    .map((n) => (n || "").toLowerCase())
                    .filter((n) => n === "esso" || n === "pipeline");
                  const hasEsso = legacyLower.includes("esso");
                  const hasPipeline = legacyLower.includes("pipeline");
                  const lanes = customer?.authorized_lanes || [];
                  const isAuthorized = (cc) => lanes.some(
                    (l) => (l.network || "").toLowerCase() === "petro_pass"
                           && (l.country || "").toUpperCase() === cc,
                  );
                  const ppCaAuth = isAuthorized("CA");
                  const ppUsAuth = isAuthorized("US");
                  return (
                    <select
                      className="gf-input"
                      value={network || "both"}
                      onChange={(e) => setNetwork(e.target.value)}
                      data-testid="report-network-filter"
                    >
                      <optgroup label="Legacy price report networks">
                        {hasEsso && hasPipeline && (
                          <option value="both">Both (Esso + Pipeline)</option>
                        )}
                        {hasEsso && (
                          <option value="esso">Esso only</option>
                        )}
                        {hasPipeline && (
                          <option value="pipeline">Pipeline only</option>
                        )}
                      </optgroup>
                      <optgroup label="Petro-Pass customer price access">
                        <option
                          value="petro_pass_ca"
                          data-testid="report-network-opt-petro-pass-ca"
                          data-authorized={ppCaAuth ? "true" : "false"}
                        >
                          {ppCaAuth
                            ? "Petro-Pass Canada · CAD/L"
                            : "Petro-Pass Canada · CAD/L — Admin preview only · Customer not authorized"}
                        </option>
                        <option
                          value="petro_pass_us"
                          data-testid="report-network-opt-petro-pass-us"
                          data-authorized={ppUsAuth ? "true" : "false"}
                        >
                          {ppUsAuth
                            ? "Petro-Pass USA · USD/GAL"
                            : "Petro-Pass USA · USD/GAL — Admin preview only · Customer not authorized"}
                        </option>
                      </optgroup>
                    </select>
                  );
                })()}
              </div>
            )}
            <div className="flex items-center gap-1">
              <span className="overline">FROM</span>
              <input type="date" className="gf-input" value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)} data-testid="report-date-from" />
            </div>
            <div className="flex items-center gap-1">
              <span className="overline">TO</span>
              <input type="date" className="gf-input" value={dateTo}
                onChange={(e) => setDateTo(e.target.value)} data-testid="report-date-to" />
            </div>
            <button className="gf-btn gf-btn-outline" onClick={() => downloadReport("csv")} data-testid="report-csv-button">CSV</button>
            <button className="gf-btn gf-btn-outline" onClick={() => downloadReport("pdf")} data-testid="report-pdf-button">PDF</button>
            {isPrice && (
              <button
                className="gf-btn gf-btn-primary"
                onClick={() => setShowEmail(true)}
                data-testid="email-price-list-button"
                title={customer.billing_email ? `Email this price list to ${customer.billing_email}` : "Customer needs a billing email first"}
              >
                <EnvelopeSimple size={14}/> Email
              </button>
            )}
          </div>
        </div>
        <div className="overflow-auto flex-1">
          {isPrice && conflicts && conflicts.length > 0 && (
            <ConflictsBanner
              conflicts={conflicts}
              onConflictsChanged={onConflictsChanged}
              onRefetch={onFetch}
            />
          )}
          {isPrice && missingDates.length > 0 && (
            <div
              data-testid="price-report-fallback-banner"
              className="m-4 p-3 border border-amber-300 bg-amber-50 text-amber-900 text-xs font-mono"
            >
              <div className="font-bold tracking-widest mb-1">⚠ NO PRICE LIST UPLOADED FOR:</div>
              <div className="mb-1">{missingDates.join(" · ")}</div>
              <div className="text-amber-800/80">
                Showing the most recent price list on or before each missing date. Upload a CSV with the matching effective date to get true date-specific prices.
              </div>
            </div>
          )}
          {loading ? (
            <div className="p-8 text-center font-mono text-sm text-zinc-500">BUILDING REPORT…</div>
          ) : !rows || rows.length === 0 ? (
            <div className="p-8 text-center text-zinc-500">No data between {dateFrom} and {dateTo}.</div>
          ) : (
            <table className="gf-table">
              <thead><tr>{cols.map((c) => <th key={c}>{c}</th>)}</tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    {cols.map((c) => (
                      <td key={c} className={typeof r[c] === "number" ? "gf-num" : "text-xs"}>
                        {typeof r[c] === "number"
                          ? (isPrice && ["PRICE/LTR.", "PRICE/GAL.", "GST/HST/FNT", "PST/QST", "TOTAL PRICE"].includes(c)
                              ? `$${r[c].toFixed(4)}`
                              : r[c].toFixed(2))
                          : r[c]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="p-4 border-t border-zinc-200 flex justify-between items-center">
          <div className="overline">
            {isPrice
              ? (isPPUS
                  ? "PETRO-PASS USA · USD/GAL supplier price + customer margin · no Canadian tax breakdown"
                  : `ESSO: DIESEL LS + DSL EFF LS · PIPELINE: Diesel only · Taxes computed per station province (AB 5%, ON 13%, QC GST+QST etc)`)
              : `Shows margin rules in range. Priority: station → city → province → default. Empty date range → current snapshot.`}
          </div>
          <button className="gf-btn gf-btn-outline" onClick={onClose}>Close</button>
        </div>
      </div>
      {showEmail && isPrice && (
        <EmailSendModal
          kind="price_list"
          customer={customer}
          recipient={customer.billing_email || customer.email}
          defaultSubject={priceListDefaultSubject(customer.company_name)}
          defaultBody={priceListDefaultBody(customer.company_name)}
          onSend={async (overrides) => {
            const { data } = await api.post(`/customers/${customerId}/email-price-list`, {
              date_from: dateFrom,
              date_to: dateTo,
              network: network && network !== "both" ? network : null,
              subject_override: isEditedPriceListSubject(overrides.subject, customer.company_name) ? overrides.subject || null : null,
              body_override: overrides.body !== priceListDefaultBody(customer.company_name) ? overrides.body || null : null,
              cc_operator: overrides.cc,
            });
            return data;
          }}
          onClose={() => { setShowEmail(false); loadEmailHistory(); }}
          history={emailHistory}
        />
      )}
    </div>
  );
}




// ---------- RESOLVE-SHADOWS MODAL ----------
// Bulk-deletes station-scope rules that the parent flagged as "shadowed" — a
// newer city rule wins for the same network/product/city, so the station rule
// never fires. Shows a per-rule preview before deleting; deletes are
// destructive so we keep the confirmation explicit.
function ShadowResolveModal({ shadowedRules, onClose, onResolved }) {
  const [busy, setBusy] = React.useState(false);
  const [excluded, setExcluded] = React.useState(new Set());
  const toResolve = shadowedRules.filter((s) => !excluded.has(s.rule.margin_id));

  const toggle = (id) => {
    const next = new Set(excluded);
    if (next.has(id)) next.delete(id); else next.add(id);
    setExcluded(next);
  };

  const resolve = async () => {
    if (toResolve.length === 0) return;
    setBusy(true);
    let ok = 0, fail = 0;
    for (const s of toResolve) {
      try {
        await api.delete(`/margins/${s.rule.margin_id}`);
        ok += 1;
      } catch (_e) {
        fail += 1;
      }
    }
    if (ok > 0) toast.success(`Removed ${ok} shadowed rule${ok === 1 ? "" : "s"}${fail ? ` · ${fail} failed` : ""}`);
    if (ok === 0 && fail > 0) toast.error("All deletes failed");
    setBusy(false);
    onResolved();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !busy && onClose()}>
      <div
        className="bg-white w-full max-w-3xl border border-zinc-200 max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="resolve-shadows-modal"
      >
        <div className="gf-card-header">
          <h3>Resolve shadowed rules</h3>
          <span className="text-xs text-zinc-500 font-mono">{toResolve.length} of {shadowedRules.length} selected</span>
        </div>
        <div className="overflow-auto flex-1 p-5 space-y-3">
          <div className="text-sm text-zinc-600">
            These station-level rules are shadowed by newer city-level rules —
            they never take effect on transactions or price reports. Untick any
            rule you want to keep, then click Delete to clean up the rest.
          </div>
          <table className="gf-table">
            <thead>
              <tr>
                <th style={{ width: 32 }}></th>
                <th>STATION RULE</th>
                <th>CITY</th>
                <th className="text-right">CPL</th>
                <th>EFFECTIVE</th>
                <th>NEW CITY RULE</th>
                <th className="text-right">CPL</th>
                <th>EFFECTIVE</th>
              </tr>
            </thead>
            <tbody>
              {shadowedRules.map(({ rule, shadowedBy }) => {
                const inSet = !excluded.has(rule.margin_id);
                return (
                  <tr key={rule.margin_id} className={inSet ? "" : "bg-zinc-50 text-zinc-400"} data-testid={`shadow-row-${rule.margin_id}`}>
                    <td>
                      <input
                        type="checkbox"
                        checked={inSet}
                        onChange={() => toggle(rule.margin_id)}
                        data-testid={`shadow-row-toggle-${rule.margin_id}`}
                      />
                    </td>
                    <td className="text-xs">
                      <div className="font-mono">{rule.station_id}</div>
                      <div className="text-zinc-500">{rule.station_name || ""}</div>
                    </td>
                    <td className="font-mono text-xs">{(rule.station_city || "").toUpperCase()}</td>
                    <td className="gf-num">{Number(rule.margin_cpl ?? 0).toFixed(2)}</td>
                    <td className="font-mono text-xs">{rule.effective_date}</td>
                    <td className="font-mono text-xs">{(shadowedBy.city || "").toUpperCase()}</td>
                    <td className="gf-num">{Number(shadowedBy.margin_cpl ?? 0).toFixed(2)}</td>
                    <td className="font-mono text-xs">{shadowedBy.effective_date}</td>
                  </tr>
                );
              })}
              {shadowedRules.length === 0 && (
                <tr><td colSpan={8} className="text-center text-zinc-400 py-6">No shadowed rules — you're clean.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="p-4 border-t border-zinc-200 flex justify-between items-center">
          <div className="text-xs text-zinc-500 font-mono">
            Deletes only the STATION rules. The city rules they were shadowed by remain.
          </div>
          <div className="flex gap-2">
            <button className="gf-btn gf-btn-outline" onClick={onClose} disabled={busy} data-testid="shadow-cancel">Cancel</button>
            <button
              className="gf-btn gf-btn-primary"
              onClick={resolve}
              disabled={busy || toResolve.length === 0}
              style={{ background: "#b45309", borderColor: "#b45309" }}
              data-testid="shadow-confirm"
            >
              {busy ? "Deleting…" : `Delete ${toResolve.length} rule${toResolve.length === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}



// ---------- CONFLICTS BANNER (Price Report) ----------
// Shown above the price-report preview when a margin rule with effective_date
// inside the requested range has an OLDER rule for the same scope/product/
// location. The default action is "Keep both" (per Feb 2026 spec) — the
// priority engine already resolves to the most recent rule. The banner is
// purely informational + offers a per-row "Use yesterday's instead (delete
// new rule)" link in case the admin wants to roll back without leaving the page.
function ConflictsBanner({ conflicts, onConflictsChanged, onRefetch }) {
  const [expanded, setExpanded] = React.useState(false);
  const [busy, setBusy] = React.useState(null);   // margin_id currently being deleted
  const deleteNew = async (c) => {
    if (!window.confirm(
      `Delete the NEW rule for ${describeConflict(c)} effective ${c.new_effective_date}?\n\n` +
      `The previous rule (${c.prev_margin_cpl} cpl, effective ${c.prev_effective_date}) ` +
      `will become the active one again.`,
    )) return;
    setBusy(c.margin_id);
    try {
      await api.delete(`/margins/${c.margin_id}`);
      toast.success("Reverted to previous rule");
      onConflictsChanged((conflicts || []).filter((x) => x.margin_id !== c.margin_id));
      onRefetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally { setBusy(null); }
  };

  return (
    <div
      data-testid="price-report-conflicts-banner"
      className="m-4 border border-amber-300 bg-amber-50 text-amber-900"
    >
      <div className="flex items-center justify-between p-3">
        <div className="text-xs font-mono">
          <span className="font-bold tracking-widest">⚠ {conflicts.length} NEW MARGIN RULE{conflicts.length === 1 ? "" : "S"} IN THIS RANGE</span>
          <span className="ml-2 text-amber-800/80 normal-case tracking-normal">
            {" "}override an earlier rule for the same scope/product/location. Both rules are kept — the most recent applies. Review below if you'd like to revert any of them.
          </span>
        </div>
        <button
          type="button"
          className="text-xs font-mono uppercase tracking-widest hover:underline"
          onClick={() => setExpanded((x) => !x)}
          data-testid="conflicts-toggle"
        >
          {expanded ? "Hide details" : "Show details"}
        </button>
      </div>
      {expanded && (
        <div className="border-t border-amber-200">
          <table className="gf-table">
            <thead>
              <tr>
                <th>SCOPE</th>
                <th>NET / PRODUCT</th>
                <th>TARGET</th>
                <th className="text-right">PREV CPL</th>
                <th>PREV DATE</th>
                <th className="text-right">NEW CPL</th>
                <th>NEW DATE</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {conflicts.map((c) => (
                <tr key={c.margin_id} data-testid={`conflict-row-${c.margin_id}`}>
                  <td className="font-mono text-xs uppercase">{c.scope}</td>
                  <td className="font-mono text-xs">{(c.network || "").toUpperCase()} · {c.product_canonical}</td>
                  <td className="text-xs">
                    {c.scope === "station" ? c.station_id :
                     c.scope === "city" ? c.city :
                     c.scope === "province" ? c.province : "—"}
                  </td>
                  <td className="gf-num">{(c.prev_margin_cpl ?? 0).toFixed(2)}</td>
                  <td className="font-mono text-xs">{c.prev_effective_date}</td>
                  <td className="gf-num">{(c.new_margin_cpl ?? 0).toFixed(2)}</td>
                  <td className="font-mono text-xs">{c.new_effective_date}</td>
                  <td>
                    <button
                      className="text-xs underline hover:no-underline disabled:opacity-50"
                      onClick={() => deleteNew(c)}
                      disabled={busy === c.margin_id}
                      data-testid={`conflict-revert-${c.margin_id}`}
                      title="Delete the new rule and use yesterday's"
                    >
                      {busy === c.margin_id ? "Reverting…" : "Use yesterday's"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function describeConflict(c) {
  if (c.scope === "station") return `station ${c.station_id}`;
  if (c.scope === "city") return `city ${c.city}`;
  if (c.scope === "province") return `province ${c.province}`;
  return `${(c.network || "").toUpperCase()} ${c.product_canonical} default`;
}



// ---------- Delete customer modal ----------
// Shows the count of every related record (cards / trucks / drivers / margin
// rules / transactions / invoices / payments / portal users) and asks the
// admin to tick each category they want cascaded. The DELETE call sends one
// query flag per ticked category — the backend refuses the call if any
// non-empty category isn't ticked, so the UI never silently leaks foreign
// keys.
const DELETE_CATEGORIES = [
  { key: "cards",            label: "Fuel cards" },
  { key: "trucks",           label: "Trucks" },
  { key: "drivers",          label: "Drivers" },
  { key: "margin_rules",     label: "Margin rules" },
  { key: "transactions",     label: "Transactions" },
  { key: "invoices",         label: "Invoices" },
  { key: "invoice_payments", label: "Invoice payments" },
  { key: "customer_users",   label: "Portal users" },
];

function DeleteCustomerModal({ customer, onClose, onDeleted }) {
  const [preview, setPreview] = React.useState(null);
  const [picks, setPicks] = React.useState({});
  const [confirmText, setConfirmText] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    api
      .get(`/customers/${customer.customer_id}/delete-preview`)
      .then(({ data }) => setPreview(data))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load preview"));
  }, [customer.customer_id]);

  const totalRelated = preview ? Object.values(preview.counts).reduce((a, n) => a + n, 0) : 0;
  const allTicked = preview
    ? DELETE_CATEGORIES.every((c) => preview.counts[c.key] === 0 || picks[c.key])
    : false;

  const run = async () => {
    if (confirmText !== "DELETE") return toast.error("Type DELETE to confirm");
    setBusy(true);
    try {
      const params = new URLSearchParams({ confirm: "DELETE" });
      for (const c of DELETE_CATEGORIES) if (picks[c.key]) params.set(c.key, "true");
      await api.delete(`/customers/${customer.customer_id}?${params.toString()}`);
      onDeleted();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : (detail?.message || "Delete failed");
      toast.error(msg);
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4" onClick={() => !busy && onClose()}>
      <div className="bg-white w-full max-w-lg border border-red-300" onClick={(e) => e.stopPropagation()}>
        <div className="gf-card-header bg-red-50 border-b border-red-200">
          <h3 className="text-red-900">Delete customer</h3>
        </div>
        <div className="gf-card-body text-sm space-y-3">
          <div className="text-zinc-700">
            <div className="font-semibold">{customer.company_name}</div>
            <div className="font-mono text-xs text-zinc-500">{customer.customer_id}</div>
          </div>

          {!preview ? (
            <div className="text-zinc-400 italic">Loading related records…</div>
          ) : totalRelated === 0 ? (
            <div className="text-xs text-zinc-600 p-3 bg-zinc-50 border border-zinc-200">
              This customer has no related records. Click <b>Delete customer</b> to remove just the profile.
            </div>
          ) : (
            <>
              <div className="text-xs text-zinc-700">
                This customer has linked records. Tick each category you want to delete along with the customer.
                Untouched categories will block the delete.
              </div>
              <div className="space-y-1 border border-zinc-200 p-3">
                {DELETE_CATEGORIES.map((c) => {
                  const n = preview.counts[c.key] || 0;
                  if (n === 0) return null;
                  return (
                    <label key={c.key} className="flex items-center justify-between gap-3 cursor-pointer text-xs hover:bg-zinc-50 px-1 py-0.5">
                      <span className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={!!picks[c.key]}
                          onChange={(e) => setPicks({ ...picks, [c.key]: e.target.checked })}
                          data-testid={`delete-pick-${c.key}`}
                        />
                        <span>{c.label}</span>
                      </span>
                      <span className="font-mono text-zinc-500">{n}</span>
                    </label>
                  );
                })}
              </div>
              <button
                type="button"
                className="text-xs text-zinc-600 underline"
                onClick={() => {
                  const all = {};
                  for (const c of DELETE_CATEGORIES) if ((preview.counts[c.key] || 0) > 0) all[c.key] = true;
                  setPicks(all);
                }}
                data-testid="delete-tick-all"
              >
                Tick all
              </button>
            </>
          )}

          <div className="mt-3">
            <div className="overline mb-1">TYPE <span className="font-mono text-red-700">DELETE</span> TO CONFIRM</div>
            <input
              type="text"
              className="gf-input"
              placeholder="DELETE"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              data-testid="delete-customer-confirm-input"
              autoFocus
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
          <button className="gf-btn gf-btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
          <button
            className="gf-btn gf-btn-danger"
            onClick={run}
            disabled={busy || confirmText !== "DELETE" || (preview && totalRelated > 0 && !allTicked)}
            data-testid="delete-customer-run"
          >
            {busy ? "Deleting…" : "Delete customer"}
          </button>
        </div>
      </div>
    </div>
  );
}
